import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  createChart,
  ColorType,
  CrosshairMode,
  CandlestickSeries,
  LineSeries,
  AreaSeries,
  HistogramSeries,
  LineStyle,
} from 'lightweight-charts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faChartLine,
  faPlus,
  faTimes,
  faSync,
  faPlay,
  faPause,
  faExpand,
  faCompress,
  faArrowUp,
  faArrowDown,
  faBell,
} from '@fortawesome/free-solid-svg-icons';

import { marketDataApi } from '../api/marketDataApi';
import { orderApi } from '../api/orderApi';
import { accountApi } from '../api/accountApi';
import { schwabApi } from '../api/schwabApi';
import { useRisk } from '../contexts/RiskContext';
import { useAuth } from '../contexts/AuthContext';
import { useAlerts } from '../contexts/AlertsContext';
import { formatEtChartTick, formatEtCrosshairTime, formatEtDateTime } from '../utils/etTime';

const CHART_QTY_KEY = 'lt-chart-order-qty';
const VENUE_STORAGE_KEY = 'lt-order-venue';
const SCHWAB_ACCOUNT_STORAGE_KEY = 'lt-order-schwab-account';
const DAY_TAPE_OPEN_KEY = 'lt-chart-day-tape-open';
const LIVE_INTERVALS = [2, 5, 10, 15, 30, 60];

const TRADE_MODES = [
  { id: 'mkt', label: 'Mkt' },
  { id: 'lmt', label: 'Lmt' },
  { id: 'brkt', label: 'Brkt' },
  { id: 'tp', label: 'TP' },
  { id: 'alert', label: 'Alrt' },
];

const SCHWAB_ORDER_TYPE = {
  market: 'MARKET',
  limit: 'LIMIT',
  stop: 'STOP',
  stop_limit: 'STOP_LIMIT',
};

function loadDayTapeOpen() {
  try {
    return localStorage.getItem(DAY_TAPE_OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

/** YYYY-MM-DD in America/New_York */
function etTodayYmd() {
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
        .formatToParts(new Date())
        .filter((p) => p.type !== 'literal')
        .map((p) => [p.type, p.value])
    );
    return `${parts.year}-${parts.month}-${parts.day}`;
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function fillCashDelta(fill) {
  const qty = Number(fill.qty) || 0;
  const price = Number(fill.price) || 0;
  if (!qty || !price) return 0;
  return fill.side === 'sell' ? qty * price : -(qty * price);
}

function summarizeDayFills(fills) {
  let buyQty = 0;
  let sellQty = 0;
  let buyNotional = 0;
  let sellNotional = 0;
  for (const f of fills) {
    const qty = Number(f.qty) || 0;
    const price = Number(f.price) || 0;
    if (f.side === 'buy') {
      buyQty += qty;
      buyNotional += qty * price;
    } else if (f.side === 'sell') {
      sellQty += qty;
      sellNotional += qty * price;
    }
  }
  // FIFO realized on today's fills only (does not include overnight cost basis)
  const lots = [];
  let realized = 0;
  const chrono = [...fills].sort((a, b) => (a.ts || 0) - (b.ts || 0));
  for (const f of chrono) {
    let qty = Number(f.qty) || 0;
    const price = Number(f.price) || 0;
    if (qty <= 0 || price <= 0) continue;
    if (f.side === 'buy') {
      lots.push({ qty, price });
      continue;
    }
    while (qty > 0 && lots.length) {
      const lot = lots[0];
      const take = Math.min(lot.qty, qty);
      realized += (price - lot.price) * take;
      lot.qty -= take;
      qty -= take;
      if (lot.qty <= 1e-9) lots.shift();
    }
  }
  return {
    count: fills.length,
    buyQty,
    sellQty,
    buyNotional,
    sellNotional,
    cash: sellNotional - buyNotional,
    avgBuy: buyQty > 0 ? buyNotional / buyQty : null,
    avgSell: sellQty > 0 ? sellNotional / sellQty : null,
    realized,
  };
}

function normalizeAlpacaFill(a, symbol) {
  const side = String(a.side || '').toLowerCase();
  if (side !== 'buy' && side !== 'sell') return null;
  const qty = Number(a.qty);
  const price = Number(a.price);
  if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(price) || price <= 0) return null;
  const ts = a.transaction_time || a.timestamp || a.date || null;
  return {
    id: String(a.id || a.activity_id || `${side}-${ts}-${qty}-${price}`),
    symbol: String(a.symbol || symbol).toUpperCase(),
    side,
    qty,
    price,
    ts: ts ? new Date(ts).getTime() : Date.now(),
    source: 'alpaca',
  };
}

function normalizeSchwabTrade(t, symbol) {
  const qtyRaw = Number(t.quantity);
  const price = Number(t.price);
  if (!Number.isFinite(qtyRaw) || qtyRaw === 0 || !Number.isFinite(price) || price <= 0) return null;
  const qty = Math.abs(qtyRaw);
  const desc = String(t.description || '').toLowerCase();
  let side = qtyRaw < 0 ? 'sell' : 'buy';
  if (desc.includes('sell')) side = 'sell';
  else if (desc.includes('buy')) side = 'buy';
  const ts = t.time || t.tradeDate || null;
  return {
    id: String(t.activityId || t.orderId || `${side}-${ts}-${qty}-${price}`),
    symbol: String(t.symbol || symbol).toUpperCase(),
    side,
    qty,
    price,
    ts: ts ? new Date(ts).getTime() : Date.now(),
    source: 'schwab',
  };
}

function normalizeSchwabFilledOrder(o, symbol) {
  const status = String(o.status || '').toUpperCase();
  if (!status.includes('FILL')) return null;
  const qty = Number(o.filledQuantity ?? o.quantity);
  const price = Number(o.price);
  if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(price) || price <= 0) return null;
  const instr = String(o.instruction || '').toUpperCase();
  const side = instr.includes('SELL') ? 'sell' : 'buy';
  const ts = o.closeTime || o.enteredTime || null;
  return {
    id: String(o.orderId || `${side}-${ts}-${qty}-${price}`),
    symbol: String(o.symbol || symbol).toUpperCase(),
    side,
    qty,
    price,
    ts: ts ? new Date(ts).getTime() : Date.now(),
    source: 'schwab',
  };
}

function DayTapePanel({
  open,
  onClose,
  symbol,
  fills,
  summary,
  loading,
  onRefresh,
  venue,
  intradayPl,
}) {
  if (!open) return null;
  return (
    <div className="w-[220px] shrink-0 self-stretch border-l border-white/10 bg-[#0E131B] flex flex-col">
      <div className="px-2.5 py-2 border-b border-white/10 flex items-center justify-between gap-2">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#6B7289] font-semibold">Day tape</div>
          <div className="text-[10px] text-[#8B93A7] font-mono">{symbol} · ET</div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onRefresh}
            className="h-6 w-6 rounded text-[#6B7289] hover:text-[#E8EDF7] hover:bg-white/5"
            title="Refresh day fills"
          >
            <FontAwesomeIcon icon={loading ? faSpinner : faSync} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="h-6 w-6 rounded text-[#6B7289] hover:text-[#E8EDF7] hover:bg-white/5"
            title="Hide day tape"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      <div className="px-2.5 py-2 border-b border-white/10 space-y-1">
        <div className="flex justify-between font-mono text-[10px] tabular-nums">
          <span className="text-[#6B7289]">Buys</span>
          <span className="text-[#3DDC97]">
            {summary.buyQty || 0}
            {summary.avgBuy != null ? ` @ ${formatPrice(summary.avgBuy)}` : ''}
          </span>
        </div>
        <div className="flex justify-between font-mono text-[10px] tabular-nums">
          <span className="text-[#6B7289]">Sells</span>
          <span className="text-[#F07167]">
            {summary.sellQty || 0}
            {summary.avgSell != null ? ` @ ${formatPrice(summary.avgSell)}` : ''}
          </span>
        </div>
        <div className="flex justify-between font-mono text-[10px] tabular-nums">
          <span className="text-[#6B7289]">Cash Δ</span>
          <span className={summary.cash >= 0 ? 'text-[#3DDC97]' : 'text-[#F07167]'}>
            {summary.cash >= 0 ? '+' : ''}
            {formatPrice(summary.cash)}
          </span>
        </div>
        <div className="flex justify-between font-mono text-[10px] tabular-nums">
          <span className="text-[#6B7289]" title="FIFO on today's fills only">
            Day R
          </span>
          <span className={summary.realized >= 0 ? 'text-[#3DDC97]' : 'text-[#F07167]'}>
            {summary.realized >= 0 ? '+' : ''}
            {formatPrice(summary.realized)}
          </span>
        </div>
        {intradayPl != null && Number.isFinite(intradayPl) ? (
          <div className="flex justify-between font-mono text-[10px] tabular-nums">
            <span className="text-[#6B7289]">Intraday</span>
            <span className={intradayPl >= 0 ? 'text-[#3DDC97]' : 'text-[#F07167]'}>
              {intradayPl >= 0 ? '+' : ''}
              {formatPrice(intradayPl)}
            </span>
          </div>
        ) : null}
        <div className="text-[9px] text-[#4B5568]">{venue === 'schwab' ? 'Schwab' : 'Alpaca'} fills today</div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {loading && fills.length === 0 ? (
          <div className="px-2.5 py-4 text-[11px] text-[#6B7289] text-center">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
            Loading…
          </div>
        ) : fills.length === 0 ? (
          <div className="px-2.5 py-4 text-[11px] text-[#6B7289] text-center">No fills yet today</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {fills.map((f) => (
              <li key={f.id} className="px-2.5 py-1.5 font-mono text-[10px] tabular-nums">
                <div className="flex items-center justify-between gap-1">
                  <span className={f.side === 'buy' ? 'text-[#3DDC97]' : 'text-[#F07167]'}>
                    {f.side === 'buy' ? 'BUY' : 'SELL'}
                  </span>
                  <span className="text-[#6B7289]">
                    {f.ts ? formatEtDateTime(f.ts).slice(11, 16) : '—'}
                  </span>
                </div>
                <div className="flex justify-between text-[#E8EDF7] mt-0.5">
                  <span>
                    {f.qty} × {formatPrice(f.price)}
                  </span>
                  <span className={fillCashDelta(f) >= 0 ? 'text-[#3DDC97]' : 'text-[#8B93A7]'}>
                    {fillCashDelta(f) >= 0 ? '+' : ''}
                    {formatPrice(fillCashDelta(f))}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function loadChartQty() {
  try {
    const n = Number(localStorage.getItem(CHART_QTY_KEY));
    if (Number.isFinite(n) && n > 0) return String(n);
  } catch {
    /* ignore */
  }
  return '10';
}

function loadChartVenue() {
  try {
    return localStorage.getItem(VENUE_STORAGE_KEY) === 'schwab' ? 'schwab' : 'alpaca';
  } catch {
    return 'alpaca';
  }
}

function loadSchwabAccount() {
  try {
    return localStorage.getItem(SCHWAB_ACCOUNT_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function maskAccount(num) {
  if (!num) return '—';
  const s = String(num);
  return s.length <= 4 ? s : `••••${s.slice(-4)}`;
}

function mapAlpacaToSchwabPayload(orderData, accountNumber) {
  const payload = {
    accountNumber: String(accountNumber),
    symbol: orderData.symbol,
    instruction: orderData.side === 'sell' ? 'SELL' : 'BUY',
    quantity: Number(orderData.qty),
    orderType: SCHWAB_ORDER_TYPE[orderData.type] || 'MARKET',
    duration: 'DAY',
    session: 'NORMAL',
    assetType: 'EQUITY',
  };
  if (orderData.limitPrice != null) payload.price = orderData.limitPrice;
  if (orderData.stopPrice != null) payload.stopPrice = orderData.stopPrice;
  return payload;
}

function normalizeL1Quote(raw, symbol) {
  if (!raw || typeof raw !== 'object') return null;
  const bid = Number(raw.bid ?? raw.bidPrice ?? raw.bp);
  const ask = Number(raw.ask ?? raw.askPrice ?? raw.ap);
  const bidSize = Number(raw.bidSize ?? raw.bs ?? 0);
  const askSize = Number(raw.askSize ?? raw.as ?? 0);
  const hasBid = Number.isFinite(bid) && bid > 0;
  const hasAsk = Number.isFinite(ask) && ask > 0;
  if (!hasBid && !hasAsk) return null;
  const mid =
    hasBid && hasAsk
      ? (bid + ask) / 2
      : hasAsk
        ? ask
        : bid;
  const spread = hasBid && hasAsk ? ask - bid : null;
  return {
    symbol: String(raw.symbol || symbol || '').toUpperCase(),
    bid: hasBid ? bid : null,
    ask: hasAsk ? ask : null,
    bidSize: Number.isFinite(bidSize) ? bidSize : 0,
    askSize: Number.isFinite(askSize) ? askSize : 0,
    mid: Number.isFinite(mid) ? mid : null,
    spread: spread != null && Number.isFinite(spread) ? spread : null,
    timestamp: raw.timestamp || raw.t || null,
    source: raw.source || 'rest',
  };
}

function Level1Panel({
  quote,
  live,
  symbol,
  orderQty,
  onQtyChange,
  tradeMode,
  onTradeModeChange,
  limitPrice,
  onLimitPriceChange,
  tpPrice,
  onTpPriceChange,
  slPrice,
  onSlPriceChange,
  alertType,
  onAlertTypeChange,
  alertThreshold,
  onAlertThresholdChange,
  onBuy,
  onSell,
  onFlatten,
  onSetAlert,
  onSnapPrices,
  orderBusy,
  flattenBusy,
  position,
  executionVenue,
  tradeDisabled,
  flattenDisabled,
  dayTapeOpen,
  onToggleDayTape,
  daySummary,
}) {
  const maxSize = Math.max(quote?.bidSize || 0, quote?.askSize || 0, 1);
  const bidPct = quote ? Math.round(((quote.bidSize || 0) / maxSize) * 100) : 0;
  const askPct = quote ? Math.round(((quote.askSize || 0) / maxSize) * 100) : 0;
  const spreadBps =
    quote?.spread != null && quote?.mid ? (quote.spread / quote.mid) * 10000 : null;
  const posQty = position ? Number(position.qty) : null;
  const posPl = position ? Number(position.unrealizedPl || 0) : null;
  const dayCash = daySummary?.cash ?? 0;
  const dayCount = daySummary?.count ?? 0;
  const isLong = posQty != null && posQty > 0;
  const advancedOk = executionVenue === 'alpaca';
  const fieldCls =
    'w-full h-6 px-1.5 rounded bg-[#161B26] border border-white/10 text-[#E8EDF7] font-mono text-[10px] tabular-nums';

  return (
    <div className="w-[188px] shrink-0 self-stretch border-l border-white/10 bg-[#0E131B] px-2 py-2 flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <div className="text-[9px] uppercase tracking-wider text-[#6B7289] font-semibold">Level 1</div>
        <div
          className={`text-[9px] uppercase tracking-wider ${
            live ? 'text-[#3DDC97]' : 'text-[#6B7289]'
          }`}
        >
          {live ? 'Live' : quote ? 'REST' : '—'}
        </div>
      </div>

      {quote ? (
        <div className="grid grid-cols-2 gap-1.5">
          <div>
            <div className="text-[8px] uppercase tracking-wider text-[#3DDC97]/opacity-80">Bid</div>
            <div className="font-mono text-[13px] font-semibold text-[#3DDC97] tabular-nums leading-tight">
              {formatPrice(quote.bid)}
            </div>
            <div className="font-mono text-[9px] text-[#8B93A7] tabular-nums">x {quote.bidSize || 0}</div>
            <div className="mt-1 h-0.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full bg-[#3DDC97]/70 rounded-full" style={{ width: `${bidPct}%` }} />
            </div>
          </div>
          <div className="text-right">
            <div className="text-[8px] uppercase tracking-wider text-[#F07167]/opacity-80">Ask</div>
            <div className="font-mono text-[13px] font-semibold text-[#F07167] tabular-nums leading-tight">
              {formatPrice(quote.ask)}
            </div>
            <div className="font-mono text-[9px] text-[#8B93A7] tabular-nums">{quote.askSize || 0} x</div>
            <div className="mt-1 h-0.5 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full bg-[#F07167]/70 rounded-full ml-auto"
                style={{ width: `${askPct}%` }}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="text-[10px] text-[#6B7289]">No quote yet</div>
      )}

      <div className="flex justify-between font-mono text-[9px] tabular-nums text-[#8B93A7]">
        <span>
          Mid <b className="text-[#E8EDF7] font-medium">{formatPrice(quote?.mid)}</b>
        </span>
        <span>
          {quote?.spread == null ? '—' : formatPrice(quote.spread, 4)}
          {spreadBps != null ? <span className="text-[#6B7289]"> · {spreadBps.toFixed(0)}bps</span> : null}
        </span>
      </div>

      <button
        type="button"
        onClick={onToggleDayTape}
        className={`w-full rounded border px-1.5 py-1 text-left ${
          dayTapeOpen ? 'border-[#6C8CFF]/50 bg-[#6C8CFF]/10' : 'border-white/10 hover:bg-white/[0.04]'
        }`}
      >
        <div className="flex justify-between text-[8px] uppercase tracking-wider text-[#6B7289]">
          <span>Day</span>
          <span>{dayTapeOpen ? 'Hide' : 'Show'}</span>
        </div>
        <div className="font-mono text-[9px] tabular-nums">
          {dayCount === 0 ? (
            <span className="text-[#6B7289]">No fills</span>
          ) : (
            <>
              <span className="text-[#3DDC97]">{daySummary.buyQty || 0}B</span>
              <span className="text-[#6B7289]">/</span>
              <span className="text-[#F07167]">{daySummary.sellQty || 0}S</span>
              <span className="text-[#6B7289]"> · </span>
              <span className={dayCash >= 0 ? 'text-[#3DDC97]' : 'text-[#F07167]'}>
                {dayCash >= 0 ? '+' : ''}
                {formatPrice(dayCash)}
              </span>
            </>
          )}
        </div>
      </button>

      <div className="border-t border-white/10 pt-1.5 mt-auto flex flex-col gap-1">
        <div className="grid grid-cols-5 gap-0.5">
          {TRADE_MODES.map((m) => {
            const softDisable = m.id === 'brkt' && !advancedOk;
            return (
              <button
                key={m.id}
                type="button"
                disabled={softDisable}
                onClick={() => onTradeModeChange(m.id)}
                className={`h-6 rounded text-[9px] font-semibold disabled:opacity-30 ${
                  tradeMode === m.id
                    ? 'bg-[#6C8CFF] text-white'
                    : 'bg-white/[0.04] text-[#8B93A7] hover:bg-white/[0.08]'
                }`}
                title={
                  m.id === 'brkt' && !advancedOk
                    ? 'Bracket requires Alpaca'
                    : m.id === 'tp'
                      ? 'Take-profit limit sell'
                      : m.id === 'brkt'
                        ? isLong
                          ? 'OCO exit (TP + SL)'
                          : 'Entry bracket (TP + SL)'
                        : m.id === 'alert'
                          ? 'Price alert'
                          : undefined
                }
              >
                {m.label}
              </button>
            );
          })}
        </div>

        {tradeMode !== 'alert' && (
          <label className="flex items-center justify-between gap-1 text-[9px] text-[#6B7289]">
            Qty
            <input
              type="number"
              min="0.0001"
              step="1"
              value={orderQty}
              onChange={(e) => onQtyChange(e.target.value)}
              className="w-[92px] h-6 px-1.5 rounded bg-[#161B26] border border-white/10 text-[#E8EDF7] font-mono text-[10px] tabular-nums"
            />
          </label>
        )}

        {tradeMode === 'lmt' && (
          <label className="flex items-center justify-between gap-1 text-[9px] text-[#6B7289]">
            Limit
            <input
              type="number"
              step="0.01"
              value={limitPrice}
              onChange={(e) => onLimitPriceChange(e.target.value)}
              className="w-[92px] h-6 px-1.5 rounded bg-[#161B26] border border-white/10 text-[#E8EDF7] font-mono text-[10px] tabular-nums"
            />
          </label>
        )}

        {(tradeMode === 'brkt' || tradeMode === 'tp') && (
          <>
            <label className="flex items-center justify-between gap-1 text-[9px] text-[#6B7289]">
              TP
              <input
                type="number"
                step="0.01"
                value={tpPrice}
                onChange={(e) => onTpPriceChange(e.target.value)}
                className="w-[92px] h-6 px-1.5 rounded bg-[#161B26] border border-white/10 text-[#E8EDF7] font-mono text-[10px] tabular-nums"
              />
            </label>
            {tradeMode === 'brkt' && (
              <label className="flex items-center justify-between gap-1 text-[9px] text-[#6B7289]">
                SL
                <input
                  type="number"
                  step="0.01"
                  value={slPrice}
                  onChange={(e) => onSlPriceChange(e.target.value)}
                  className="w-[92px] h-6 px-1.5 rounded bg-[#161B26] border border-white/10 text-[#E8EDF7] font-mono text-[10px] tabular-nums"
                />
              </label>
            )}
            <button
              type="button"
              onClick={onSnapPrices}
              className="text-[9px] text-[#6C8CFF] hover:underline self-end"
            >
              Snap ±1% mid
            </button>
            {tradeMode === 'brkt' && (
              <div className="text-[8px] text-[#6B7289] leading-tight">
                {isLong ? 'Sell = OCO exit' : 'Buy/Sell = entry bracket'}
              </div>
            )}
            {tradeMode === 'tp' && <div className="text-[8px] text-[#6B7289]">Limit sell at TP</div>}
          </>
        )}

        {tradeMode === 'alert' && (
          <>
            <select
              value={alertType}
              onChange={(e) => onAlertTypeChange(e.target.value)}
              className={fieldCls}
            >
              <option value="price_above">Price ≥</option>
              <option value="price_below">Price ≤</option>
              <option value="pct_up">Day % ≥</option>
              <option value="pct_down">Day % ≤</option>
            </select>
            <input
              type="number"
              step="0.01"
              value={alertThreshold}
              onChange={(e) => onAlertThresholdChange(e.target.value)}
              placeholder={alertType.startsWith('pct') ? '%' : 'Price'}
              className={fieldCls}
            />
            <button
              type="button"
              onClick={onSetAlert}
              className="h-7 rounded bg-[#E8B84A] text-[#0B0E14] text-[10px] font-bold inline-flex items-center justify-center gap-1"
            >
              <FontAwesomeIcon icon={faBell} />
              Set alert
            </button>
          </>
        )}

        {tradeMode === 'mkt' && (
          <div className="grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={onBuy}
              disabled={tradeDisabled}
              className="h-7 rounded bg-[#3DDC97] text-[#0B0E14] text-[10px] font-bold disabled:opacity-50"
            >
              Buy
            </button>
            <button
              type="button"
              onClick={onSell}
              disabled={tradeDisabled}
              className="h-7 rounded bg-[#F07167] text-white text-[10px] font-bold disabled:opacity-50"
            >
              Sell
            </button>
          </div>
        )}

        {tradeMode === 'lmt' && (
          <div className="grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={onBuy}
              disabled={tradeDisabled}
              className="h-7 rounded bg-[#3DDC97] text-[#0B0E14] text-[10px] font-bold disabled:opacity-50"
            >
              Lmt buy
            </button>
            <button
              type="button"
              onClick={onSell}
              disabled={tradeDisabled}
              className="h-7 rounded bg-[#F07167] text-white text-[10px] font-bold disabled:opacity-50"
            >
              Lmt sell
            </button>
          </div>
        )}

        {tradeMode === 'brkt' && (
          <div className="grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={onBuy}
              disabled={tradeDisabled || !advancedOk || isLong}
              className="h-7 rounded bg-[#3DDC97] text-[#0B0E14] text-[10px] font-bold disabled:opacity-40"
              title="Long entry bracket"
            >
              Brkt buy
            </button>
            <button
              type="button"
              onClick={onSell}
              disabled={tradeDisabled || !advancedOk}
              className="h-7 rounded bg-[#F07167] text-white text-[10px] font-bold disabled:opacity-40"
              title={isLong ? 'OCO take-profit + stop' : 'Short entry bracket'}
            >
              {isLong ? 'Exit OCO' : 'Brkt sell'}
            </button>
          </div>
        )}

        {tradeMode === 'tp' && (
          <button
            type="button"
            onClick={onSell}
            disabled={tradeDisabled}
            className="h-7 rounded bg-[#F07167] text-white text-[10px] font-bold disabled:opacity-50"
          >
            TP sell
          </button>
        )}

        {tradeMode !== 'alert' && (
          <button
            type="button"
            onClick={onFlatten}
            disabled={flattenDisabled}
            className="h-6 w-full rounded bg-orange-600/90 text-white text-[9px] font-bold disabled:opacity-35"
          >
            Flatten
          </button>
        )}

        {position && posQty != null ? (
          <div className="font-mono text-[9px] text-[#8B93A7] tabular-nums text-center">
            <span className="text-[8px] uppercase text-[#6B7289] mr-1">
              {executionVenue === 'schwab' ? 'SCH' : 'ALP'}
            </span>
            <span className="text-[#E8EDF7] font-semibold">
              {posQty > 0 ? '+' : ''}
              {posQty}
            </span>
            {' · '}
            <span className={posPl >= 0 ? 'text-[#3DDC97]' : 'text-[#F07167]'}>
              {posPl >= 0 ? '+' : ''}
              {posPl.toFixed(2)}
            </span>
          </div>
        ) : tradeMode !== 'alert' ? (
          <div className="text-[9px] text-[#4B5568] text-center">No position</div>
        ) : null}
      </div>
    </div>
  );
}

const INDICATOR_COLORS = ['#6C8CFF', '#E8B84A', '#3DDC97', '#F07167', '#C084FC', '#22D3EE'];

const TIMEFRAMES = [
  { value: '1Min', label: '1m' },
  { value: '5Min', label: '5m' },
  { value: '15Min', label: '15m' },
  { value: '1Hour', label: '1H' },
  { value: '1Day', label: '1D' },
  { value: '1Week', label: '1W' },
  { value: '1Month', label: '1M' },
];

const INDICATOR_TYPES = [
  { value: 'SMA', label: 'SMA' },
  { value: 'EMA', label: 'EMA' },
  { value: 'VWAP', label: 'VWAP' },
  { value: 'RSI', label: 'RSI' },
  { value: 'MACD', label: 'MACD' },
  { value: 'BB', label: 'Bollinger' },
];

/** Classic fib retracement ratios (0% = first click, 100% = second click). */
const FIB_LEVELS = [
  { ratio: 0, label: '0%', color: 'rgba(232, 184, 74, 0.95)', width: 2 },
  { ratio: 0.236, label: '23.6%', color: 'rgba(108, 140, 255, 0.75)', width: 1 },
  { ratio: 0.382, label: '38.2%', color: 'rgba(61, 220, 151, 0.75)', width: 1 },
  { ratio: 0.5, label: '50%', color: 'rgba(192, 132, 252, 0.85)', width: 1 },
  { ratio: 0.618, label: '61.8%', color: 'rgba(240, 113, 103, 0.85)', width: 1 },
  { ratio: 0.786, label: '78.6%', color: 'rgba(34, 211, 238, 0.75)', width: 1 },
  { ratio: 1, label: '100%', color: 'rgba(232, 184, 74, 0.95)', width: 2 },
];

const FIB_PREVIEW_ID = '__preview__';

/**
 * Normalize lightweight-charts Time to our bar.time key.
 * Daily bars use 'YYYY-MM-DD'; hover/click often returns BusinessDay objects.
 */
function chartTimeKey(time) {
  if (time == null) return null;
  if (typeof time === 'number' || typeof time === 'string') return time;
  if (typeof time === 'object' && time.year != null && time.month != null && time.day != null) {
    const m = String(time.month).padStart(2, '0');
    const d = String(time.day).padStart(2, '0');
    return `${time.year}-${m}-${d}`;
  }
  return null;
}

function timesAscending(a, b) {
  if (typeof a === 'string' && typeof b === 'string') return a <= b;
  return a <= b;
}

/** Snap cursor to nearer high/low (or free). */
function snapFibPrice(rawPrice, bar, mode) {
  if (!bar || mode === 'free' || rawPrice == null || !Number.isFinite(rawPrice)) {
    return { price: rawPrice, snap: null };
  }
  const high = Number(bar.high);
  const low = Number(bar.low);
  const candidates = [];
  if (Number.isFinite(high)) candidates.push({ price: high, snap: 'H' });
  if (Number.isFinite(low)) candidates.push({ price: low, snap: 'L' });
  if (mode === 'ohlc') {
    const open = Number(bar.open);
    const close = Number(bar.close);
    if (Number.isFinite(open)) candidates.push({ price: open, snap: 'O' });
    if (Number.isFinite(close)) candidates.push({ price: close, snap: 'C' });
  }
  if (!candidates.length) return { price: rawPrice, snap: null };

  let best = candidates[0];
  let bestDist = Math.abs(rawPrice - best.price);
  for (let i = 1; i < candidates.length; i += 1) {
    const d = Math.abs(rawPrice - candidates[i].price);
    if (d < bestDist) {
      best = candidates[i];
      bestDist = d;
    }
  }
  return { price: best.price, snap: best.snap };
}

function resolveDistinctTimes(t1, t2, bars) {
  let timeStart = timesAscending(t1, t2) ? t1 : t2;
  let timeEnd = timesAscending(t1, t2) ? t2 : t1;
  if (timeStart === timeEnd && bars?.length) {
    const idx = bars.findIndex((b) => b.time === timeEnd);
    if (idx >= 0 && idx < bars.length - 1) timeEnd = bars[idx + 1].time;
    else if (idx > 0) timeStart = bars[idx - 1].time;
  }
  if (bars?.length) {
    const endIdx = bars.findIndex((b) => b.time === timeEnd);
    if (endIdx >= 0) {
      const extendBy = Math.max(8, Math.floor(bars.length * 0.06));
      const stretched = bars[Math.min(bars.length - 1, endIdx + extendBy)];
      if (stretched) timeEnd = stretched.time;
    }
  }
  return { timeStart, timeEnd };
}

function isIntradayTf(tf) {
  return String(tf).includes('Min') || String(tf).includes('Hour');
}

function timeframeBucketMs(timeframe) {
  switch (timeframe) {
    case '1Min':
      return 60_000;
    case '5Min':
      return 5 * 60_000;
    case '15Min':
      return 15 * 60_000;
    case '1Hour':
      return 60 * 60_000;
    default:
      return null;
  }
}

/** Map a stream timestamp onto the chart's bar time key. */
function streamBarTime(timestamp, timeframe) {
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return null;
  if (isIntradayTf(timeframe)) {
    const bucket = timeframeBucketMs(timeframe) || 60_000;
    return Math.floor(Math.floor(d.getTime() / bucket) * bucket / 1000);
  }
  const p = etDateParts(d);
  return `${p.year}-${p.month}-${p.day}`;
}

function resolveMarketDataWsUrl(token, env) {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  let host;
  let port = '';
  if (process.env.NODE_ENV === 'production') {
    const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
    try {
      const url = new URL(apiUrl);
      host = url.hostname;
      if (url.port && url.port !== '80' && url.port !== '443') port = `:${url.port}`;
    } catch {
      host = window.location.hostname;
      if (window.location.port) port = `:${window.location.port}`;
    }
  } else {
    host = 'localhost';
    port = ':3001';
  }
  const e = env === 'live' ? 'live' : 'paper';
  return `${protocol}//${host}${port}/ws/market-data?token=${encodeURIComponent(token)}&env=${e}`;
}

function etDateParts(date) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
      .formatToParts(date)
      .filter((p) => p.type !== 'literal')
      .map((p) => [p.type, p.value])
  );
  return parts;
}

function formatPrice(n, digits = 2) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatVolume(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(Math.round(v));
}

function formatPct(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

function calculateSMA(data, period) {
  const result = [];
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push(null);
      continue;
    }
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += data[j].close;
    result.push(sum / period);
  }
  return result;
}

function calculateEMA(data, period) {
  const result = [];
  const multiplier = 2 / (period + 1);
  let ema = null;
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push(null);
      continue;
    }
    if (ema == null) {
      let sum = 0;
      for (let j = i - period + 1; j <= i; j++) sum += data[j].close;
      ema = sum / period;
    } else {
      ema = (data[i].close - ema) * multiplier + ema;
    }
    result.push(ema);
  }
  return result;
}

function calculateRSI(data, period = 14) {
  const result = [];
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 0; i < data.length; i++) {
    if (i === 0) {
      result.push(null);
      continue;
    }
    const change = data[i].close - data[i - 1].close;
    const gain = Math.max(change, 0);
    const loss = Math.max(-change, 0);
    if (i < period) {
      avgGain += gain;
      avgLoss += loss;
      result.push(null);
      continue;
    }
    if (i === period) {
      avgGain = (avgGain + gain) / period;
      avgLoss = (avgLoss + loss) / period;
    } else {
      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;
    }
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    result.push(100 - 100 / (1 + rs));
  }
  return result;
}

function calculateMACD(data, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) {
  const fastEMA = calculateEMA(data, fastPeriod);
  const slowEMA = calculateEMA(data, slowPeriod);
  const macdLine = fastEMA.map((fast, i) =>
    fast == null || slowEMA[i] == null ? null : fast - slowEMA[i]
  );
  const macdAsBars = macdLine.map((v) => ({ close: v == null ? 0 : v }));
  const signalRaw = calculateEMA(macdAsBars, signalPeriod);
  const signalLine = macdLine.map((v, i) => (v == null || signalRaw[i] == null ? null : signalRaw[i]));
  const histogram = macdLine.map((macd, i) =>
    macd == null || signalLine[i] == null ? null : macd - signalLine[i]
  );
  return { macdLine, signalLine, histogram };
}

function calculateBollingerBands(data, period = 20, stdDev = 2) {
  const sma = calculateSMA(data, period);
  const upper = [];
  const lower = [];
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1 || sma[i] == null) {
      upper.push(null);
      lower.push(null);
      continue;
    }
    let sumSquaredDiff = 0;
    for (let j = i - period + 1; j <= i; j++) {
      sumSquaredDiff += Math.pow(data[j].close - sma[i], 2);
    }
    const std = Math.sqrt(sumSquaredDiff / period);
    upper.push(sma[i] + std * stdDev);
    lower.push(sma[i] - std * stdDev);
  }
  return { upper, middle: sma, lower };
}

function calculateVWAP(data) {
  let cumTPV = 0;
  let cumV = 0;
  return data.map((bar) => {
    const tp = (bar.high + bar.low + bar.close) / 3;
    const vol = Number(bar.volume) || 0;
    cumTPV += tp * vol;
    cumV += vol;
    return cumV > 0 ? cumTPV / cumV : null;
  });
}

function formatBars(rawBars, timeframe) {
  const intraday = isIntradayTf(timeframe);
  return rawBars
    .map((bar) => {
      try {
        let utcDate;
        if (bar.timestamp !== undefined) utcDate = new Date(bar.timestamp);
        else if (bar.t) utcDate = new Date(bar.t);
        else return null;
        if (Number.isNaN(utcDate.getTime())) return null;

        let timeValue;
        if (intraday) {
          timeValue = Math.floor(utcDate.getTime() / 1000);
        } else {
          const p = etDateParts(utcDate);
          timeValue = `${p.year}-${p.month}-${p.day}`;
        }

        const open = parseFloat(bar.o ?? bar.open);
        const high = parseFloat(bar.h ?? bar.high);
        const low = parseFloat(bar.l ?? bar.low);
        const close = parseFloat(bar.c ?? bar.close);
        const volume = parseFloat(bar.v ?? bar.volume ?? 0);
        if (![open, high, low, close].every(Number.isFinite)) return null;

        return { time: timeValue, open, high, low, close, volume };
      } catch {
        return null;
      }
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (typeof a.time === 'string' && typeof b.time === 'string') return a.time.localeCompare(b.time);
      return a.time - b.time;
    })
    .filter((bar, i, arr) => i === 0 || bar.time !== arr[i - 1].time);
}

function barTimeToDate(time) {
  if (typeof time === 'number') return new Date(time * 1000);
  return new Date(`${time}T16:00:00Z`);
}

/** Inclusive end bound just before the oldest loaded bar (for fetching older history). */
function oldestBarEndIso(oldestBar) {
  if (!oldestBar) return null;
  const d = barTimeToDate(oldestBar.time);
  d.setUTCSeconds(d.getUTCSeconds() - 1);
  return d.toISOString();
}

function historyStartIso(endIso, timeframe) {
  const end = new Date(endIso);
  const start = new Date(end);
  if (timeframe === '1Min') start.setUTCDate(start.getUTCDate() - 5);
  else if (timeframe === '5Min') start.setUTCDate(start.getUTCDate() - 21);
  else if (timeframe === '15Min') start.setUTCDate(start.getUTCDate() - 45);
  else if (timeframe === '30Min' || timeframe === '1Hour') start.setUTCMonth(start.getUTCMonth() - 4);
  else if (timeframe === '1Week') start.setUTCFullYear(start.getUTCFullYear() - 8);
  else if (timeframe === '1Month') start.setUTCFullYear(start.getUTCFullYear() - 20);
  else start.setUTCFullYear(start.getUTCFullYear() - 3);
  return start.toISOString();
}

function historyPageSize(timeframe) {
  if (timeframe === '1Min' || timeframe === '5Min') return 500;
  if (timeframe === '15Min' || timeframe === '30Min' || timeframe === '1Hour') return 400;
  return 300;
}

function defaultParams(type) {
  if (type === 'RSI') return { period: 14 };
  if (type === 'MACD') return { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 };
  if (type === 'BB') return { period: 20, stdDev: 2 };
  if (type === 'VWAP') return {};
  return { period: 20 };
}

const AdvancedChartingPage = () => {
  const [searchParams] = useSearchParams();
  const { checkOrder } = useRisk();
  const { addAlert } = useAlerts();
  const { accessToken } = useAuth();
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const priceSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const indicatorsRef = useRef({});
  const fibSeriesRef = useRef({});
  const barsRef = useRef([]);
  const refreshIntervalRef = useRef(null);
  const softRefreshInFlightRef = useRef(false);
  const marketWsRef = useRef(null);
  const streamStatusRef = useRef('off');
  const indicatorThrottleRef = useRef(0);
  const barsStateThrottleRef = useRef(0);
  const timeframeRef = useRef('1Day');
  const chartTypeRef = useRef('candle');
  const loadingMoreRef = useRef(false);
  const hasMoreHistoryRef = useRef(true);
  const loadOlderBarsRef = useRef(null);
  const drawToolRef = useRef(null);
  const fibPendingRef = useRef(null);
  const fibSnapModeRef = useRef('hl');
  const fibHoverRef = useRef(null);
  // { H?: IPriceLine, L?: IPriceLine, O?: IPriceLine, C?: IPriceLine, anchor?: IPriceLine }
  const snapPriceLineRef = useRef({});
  const orderMsgTimerRef = useRef(null);

  const querySymbol = useMemo(() => {
    const raw = (searchParams.get('symbol') || '').toUpperCase().replace(/[^A-Z0-9.]/g, '');
    return raw || '';
  }, [searchParams]);

  const [symbol, setSymbol] = useState(() => querySymbol || 'AAPL');
  const [symbolInput, setSymbolInput] = useState(() => querySymbol || 'AAPL');
  const [timeframe, setTimeframe] = useState('1Day');
  const [chartType, setChartType] = useState('candle');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [bars, setBars] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [showIndicatorMenu, setShowIndicatorMenu] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState('SMA');
  const [indicatorParams, setIndicatorParams] = useState({ period: 20 });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(5);
  const [streamStatus, setStreamStatus] = useState('off'); // off | connecting | live | error
  const [streamFeed, setStreamFeed] = useState('');
  const [hud, setHud] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [drawTool, setDrawTool] = useState(null); // null | 'fib'
  const [fibPending, setFibPending] = useState(null); // { time, price, snap } after first click
  const [fibDrawings, setFibDrawings] = useState([]);
  const [fibSnapMode, setFibSnapMode] = useState('hl'); // hl | ohlc | free
  const [fibHoverHint, setFibHoverHint] = useState(null); // { snap, price }
  const [orderQty, setOrderQty] = useState(loadChartQty);
  const [executionVenue, setExecutionVenue] = useState(loadChartVenue);
  const [schwabConnected, setSchwabConnected] = useState(false);
  const [schwabAccounts, setSchwabAccounts] = useState([]);
  const [schwabAccountsLoading, setSchwabAccountsLoading] = useState(false);
  const [schwabAccountNumber, setSchwabAccountNumber] = useState(loadSchwabAccount);
  const [position, setPosition] = useState(null);
  const [orderBusy, setOrderBusy] = useState(false);
  const [flattenBusy, setFlattenBusy] = useState(false);
  const [orderMessage, setOrderMessage] = useState(null);
  const [l1Quote, setL1Quote] = useState(null);
  const [dayTapeOpen, setDayTapeOpen] = useState(loadDayTapeOpen);
  const [dayFills, setDayFills] = useState([]);
  const [dayFillsLoading, setDayFillsLoading] = useState(false);
  const [tradeMode, setTradeMode] = useState('mkt');
  const [limitPrice, setLimitPrice] = useState('');
  const [tpPrice, setTpPrice] = useState('');
  const [slPrice, setSlPrice] = useState('');
  const [alertType, setAlertType] = useState('price_above');
  const [alertThreshold, setAlertThreshold] = useState('');

  useEffect(() => {
    if (executionVenue !== 'alpaca' && tradeMode === 'brkt') setTradeMode('mkt');
  }, [executionVenue, tradeMode]);

  useEffect(() => {
    drawToolRef.current = drawTool;
  }, [drawTool]);

  useEffect(() => {
    timeframeRef.current = timeframe;
  }, [timeframe]);

  useEffect(() => {
    chartTypeRef.current = chartType;
  }, [chartType]);

  useEffect(() => {
    streamStatusRef.current = streamStatus;
  }, [streamStatus]);

  useEffect(() => {
    fibPendingRef.current = fibPending;
  }, [fibPending]);

  useEffect(() => {
    fibSnapModeRef.current = fibSnapMode;
  }, [fibSnapMode]);

  const lastBar = bars.length ? bars[bars.length - 1] : null;
  const firstBar = bars.length ? bars[0] : null;
  const changeAbs = lastBar && firstBar ? lastBar.close - firstBar.close : null;
  const changePct =
    lastBar && firstBar && firstBar.close
      ? ((lastBar.close - firstBar.close) / firstBar.close) * 100
      : null;
  const bullish = (changeAbs ?? 0) >= 0;

  const chartHeight = useMemo(
    () => (fullscreen ? Math.max(520, window.innerHeight - 220) : 620),
    [fullscreen]
  );

  useEffect(() => {
    if (!querySymbol) return;
    setSymbol(querySymbol);
    setSymbolInput(querySymbol);
  }, [querySymbol]);

  const applyMainSeriesData = useCallback(
    (data) => {
      if (!priceSeriesRef.current || !data.length) return;
      if (chartType === 'candle') {
        priceSeriesRef.current.setData(data);
      } else {
        priceSeriesRef.current.setData(data.map((b) => ({ time: b.time, value: b.close })));
      }
    },
    [chartType]
  );

  const clearIndicatorSeries = useCallback(() => {
    if (!chartRef.current) return;
    Object.values(indicatorsRef.current).forEach((series) => {
      try {
        chartRef.current.removeSeries(series);
      } catch {
        /* ignore */
      }
    });
    indicatorsRef.current = {};
  }, []);

  const clearFibSeries = useCallback((includePreview = true) => {
    if (!chartRef.current) {
      fibSeriesRef.current = {};
      return;
    }
    Object.entries(fibSeriesRef.current).forEach(([id, seriesList]) => {
      if (!includePreview && id === FIB_PREVIEW_ID) return;
      (seriesList || []).forEach((series) => {
        try {
          chartRef.current.removeSeries(series);
        } catch {
          /* ignore */
        }
      });
      delete fibSeriesRef.current[id];
    });
  }, []);

  const clearSnapPriceLine = useCallback(() => {
    const lines = snapPriceLineRef.current || {};
    if (priceSeriesRef.current) {
      Object.values(lines).forEach((line) => {
        if (!line) return;
        try {
          priceSeriesRef.current.removePriceLine(line);
        } catch {
          /* ignore */
        }
      });
    }
    snapPriceLineRef.current = {};
  }, []);

  const upsertSnapLine = useCallback((key, price, opts) => {
    if (!priceSeriesRef.current || price == null || !Number.isFinite(price)) return;
    const next = {
      price,
      color: opts.color,
      lineWidth: opts.lineWidth ?? 1,
      lineStyle: opts.lineStyle ?? LineStyle.Dotted,
      axisLabelVisible: opts.axisLabelVisible !== false,
      title: opts.title || key,
    };
    const existing = snapPriceLineRef.current[key];
    if (existing) {
      try {
        existing.applyOptions(next);
        return;
      } catch {
        snapPriceLineRef.current[key] = null;
      }
    }
    try {
      snapPriceLineRef.current[key] = priceSeriesRef.current.createPriceLine(next);
    } catch {
      snapPriceLineRef.current[key] = null;
    }
  }, []);

  /** Always show High + Low on the hovered bar; highlight the active snap. */
  const updateSnapPriceLine = useCallback(
    (price, snapLabel, bar) => {
      if (!priceSeriesRef.current || fibSnapModeRef.current === 'free') {
        clearSnapPriceLine();
        return;
      }
      const pending = fibPendingRef.current;
      const wanted = new Set();

      if (bar) {
        const high = Number(bar.high);
        const low = Number(bar.low);
        if (Number.isFinite(high)) {
          wanted.add('H');
          upsertSnapLine('H', high, {
            color: snapLabel === 'H' ? '#E8B84A' : 'rgba(61, 220, 151, 0.55)',
            lineWidth: snapLabel === 'H' ? 2 : 1,
            lineStyle: snapLabel === 'H' ? LineStyle.Solid : LineStyle.Dotted,
            title: snapLabel === 'H' ? 'Snap H' : 'High',
          });
        }
        if (Number.isFinite(low)) {
          wanted.add('L');
          upsertSnapLine('L', low, {
            color: snapLabel === 'L' ? '#E8B84A' : 'rgba(240, 113, 103, 0.55)',
            lineWidth: snapLabel === 'L' ? 2 : 1,
            lineStyle: snapLabel === 'L' ? LineStyle.Solid : LineStyle.Dotted,
            title: snapLabel === 'L' ? 'Snap L' : 'Low',
          });
        }
      } else if (price != null && Number.isFinite(price)) {
        wanted.add('active');
        upsertSnapLine('active', price, {
          color: '#E8B84A',
          lineWidth: 2,
          lineStyle: LineStyle.Solid,
          title: snapLabel ? `Snap ${snapLabel}` : 'Snap',
        });
      }

      if (pending?.price != null && Number.isFinite(pending.price)) {
        wanted.add('anchor');
        upsertSnapLine('anchor', pending.price, {
          color: 'rgba(108, 140, 255, 0.9)',
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          title: pending.snap ? `Start ${pending.snap}` : 'Start',
        });
      }

      Object.keys(snapPriceLineRef.current).forEach((key) => {
        if (wanted.has(key)) return;
        const line = snapPriceLineRef.current[key];
        if (line && priceSeriesRef.current) {
          try {
            priceSeriesRef.current.removePriceLine(line);
          } catch {
            /* ignore */
          }
        }
        delete snapPriceLineRef.current[key];
      });
    },
    [clearSnapPriceLine, upsertSnapLine]
  );

  const removeFibSeriesById = useCallback((id) => {
    const existing = fibSeriesRef.current[id];
    if (!existing || !chartRef.current) {
      delete fibSeriesRef.current[id];
      return;
    }
    (existing || []).forEach((s) => {
      try {
        chartRef.current.removeSeries(s);
      } catch {
        /* ignore */
      }
    });
    delete fibSeriesRef.current[id];
  }, []);

  const drawFibOnChart = useCallback((fib, { preview = false } = {}) => {
    if (!chartRef.current || !fib?.p1 || !fib?.p2) return;
    const bars = barsRef.current || [];
    const t1 = fib.p1.time;
    const t2 = fib.p2.time;
    const priceStart = Number(fib.p1.price);
    const priceEnd = Number(fib.p2.price);
    if (![priceStart, priceEnd].every(Number.isFinite)) return;

    const { timeStart, timeEnd } = resolveDistinctTimes(t1, t2, bars);
    const id = preview ? FIB_PREVIEW_ID : fib.id;

    // Trend must be ascending in time — right-to-left clicks used to break setData
    const trendData = timesAscending(t1, t2)
      ? [
          { time: t1 === t2 ? timeStart : t1, value: priceStart },
          { time: t1 === t2 ? timeEnd : t2, value: priceEnd },
        ]
      : [
          { time: t2, value: priceEnd },
          { time: t1, value: priceStart },
        ];

    const levelPoints = FIB_LEVELS.map((level) => {
      const price = priceStart + (priceEnd - priceStart) * level.ratio;
      return {
        level,
        price,
        data: [
          { time: timeStart, value: price },
          { time: timeEnd, value: price },
        ],
      };
    });

    const existing = fibSeriesRef.current[id];
    if (preview && existing && existing.length === FIB_LEVELS.length + 1) {
      try {
        existing[0].setData(trendData);
        levelPoints.forEach((lp, i) => {
          existing[i + 1].setData(lp.data);
          existing[i + 1].applyOptions({
            title: preview ? lp.level.label : `${lp.level.label} ${formatPrice(lp.price)}`,
          });
        });
        return;
      } catch {
        /* recreate below */
      }
    }

    if (existing) {
      existing.forEach((s) => {
        try {
          chartRef.current.removeSeries(s);
        } catch {
          /* ignore */
        }
      });
    }

    try {
      const seriesList = [];
      const trend = chartRef.current.addSeries(LineSeries, {
        color: preview ? 'rgba(232, 184, 74, 0.45)' : 'rgba(232, 184, 74, 0.6)',
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        lastValueVisible: false,
        priceLineVisible: false,
        crosshairMarkerVisible: false,
      });
      trend.setData(trendData);
      seriesList.push(trend);

      levelPoints.forEach(({ level, price, data }) => {
        const series = chartRef.current.addSeries(LineSeries, {
          color: level.color,
          lineWidth: level.width,
          lineStyle: preview ? LineStyle.Dotted : LineStyle.Solid,
          title: preview ? level.label : `${level.label} ${formatPrice(price)}`,
          lastValueVisible: !preview,
          priceLineVisible: false,
          crosshairMarkerVisible: false,
        });
        series.setData(data);
        seriesList.push(series);
      });

      fibSeriesRef.current[id] = seriesList;
    } catch (err) {
      console.error('Failed to draw fib:', err);
      removeFibSeriesById(id);
    }
  }, [removeFibSeriesById]);

  const clearFibPreview = useCallback(() => {
    removeFibSeriesById(FIB_PREVIEW_ID);
  }, [removeFibSeriesById]);

  const renderAllFibs = useCallback(() => {
    clearFibSeries(false);
    fibDrawings.forEach((fib) => drawFibOnChart(fib));
  }, [clearFibSeries, drawFibOnChart, fibDrawings]);

  const resolvePointFromParam = useCallback((param) => {
    if (!param?.point || !priceSeriesRef.current) return null;

    let rawPrice;
    try {
      rawPrice = priceSeriesRef.current.coordinateToPrice(param.point.y);
    } catch {
      return null;
    }
    if (rawPrice == null || !Number.isFinite(rawPrice)) return null;

    // Prefer explicit time; fall back to nearest bar from x if needed
    let time = chartTimeKey(param.time);
    let bar = time != null ? barsRef.current.find((b) => b.time === time) : null;

    if (!bar && param.point?.x != null && chartRef.current) {
      try {
        const t = chartRef.current.timeScale().coordinateToTime(param.point.x);
        time = chartTimeKey(t);
        if (time != null) bar = barsRef.current.find((b) => b.time === time);
      } catch {
        /* ignore */
      }
    }
    if (time == null) return null;

    if (!bar) {
      const candle = param.seriesData?.get(priceSeriesRef.current);
      if (candle && candle.high != null && candle.low != null) {
        bar = {
          open: candle.open,
          high: candle.high,
          low: candle.low,
          close: candle.close,
        };
      }
    }

    const { price, snap } = snapFibPrice(Number(rawPrice), bar, fibSnapModeRef.current);
    return { time, price, snap, bar };
  }, []);

  const drawFibOnChartRef = useRef(drawFibOnChart);
  const clearFibPreviewRef = useRef(clearFibPreview);
  const resolvePointRef = useRef(resolvePointFromParam);
  const updateSnapPriceLineRef = useRef(updateSnapPriceLine);
  const clearSnapPriceLineRef = useRef(clearSnapPriceLine);
  drawFibOnChartRef.current = drawFibOnChart;
  clearFibPreviewRef.current = clearFibPreview;
  resolvePointRef.current = resolvePointFromParam;
  updateSnapPriceLineRef.current = updateSnapPriceLine;
  clearSnapPriceLineRef.current = clearSnapPriceLine;

  const addIndicatorToChart = useCallback((indicator, data, index) => {
    if (!chartRef.current || !data.length) return;
    const color = indicator.color || INDICATOR_COLORS[index % INDICATOR_COLORS.length];

    const lineData = (values) =>
      data
        .map((bar, i) => ({ time: bar.time, value: values[i] }))
        .filter((item) => item.value != null && Number.isFinite(item.value));

    switch (indicator.type) {
      case 'SMA': {
        const values = calculateSMA(data, indicator.params.period || 20);
        const series = chartRef.current.addSeries(LineSeries, {
          color,
          lineWidth: 2,
          title: `SMA(${indicator.params.period || 20})`,
          lastValueVisible: false,
          priceLineVisible: false,
        });
        series.setData(lineData(values));
        indicatorsRef.current[`SMA_${index}`] = series;
        break;
      }
      case 'EMA': {
        const values = calculateEMA(data, indicator.params.period || 20);
        const series = chartRef.current.addSeries(LineSeries, {
          color,
          lineWidth: 2,
          title: `EMA(${indicator.params.period || 20})`,
          lastValueVisible: false,
          priceLineVisible: false,
        });
        series.setData(lineData(values));
        indicatorsRef.current[`EMA_${index}`] = series;
        break;
      }
      case 'VWAP': {
        const values = calculateVWAP(data);
        const series = chartRef.current.addSeries(LineSeries, {
          color: color || '#E8B84A',
          lineWidth: 2,
          title: 'VWAP',
          lastValueVisible: false,
          priceLineVisible: false,
        });
        series.setData(lineData(values));
        indicatorsRef.current[`VWAP_${index}`] = series;
        break;
      }
      case 'RSI': {
        const values = calculateRSI(data, indicator.params.period || 14);
        const series = chartRef.current.addSeries(LineSeries, {
          color: color || '#C084FC',
          lineWidth: 2,
          title: `RSI(${indicator.params.period || 14})`,
          priceScaleId: 'rsi',
          lastValueVisible: true,
          priceLineVisible: false,
        });
        series.setData(lineData(values));
        chartRef.current.priceScale('rsi').applyOptions({
          scaleMargins: { top: 0.78, bottom: 0 },
        });
        indicatorsRef.current[`RSI_${index}`] = series;
        break;
      }
      case 'MACD': {
        const macd = calculateMACD(
          data,
          indicator.params.fastPeriod || 12,
          indicator.params.slowPeriod || 26,
          indicator.params.signalPeriod || 9
        );
        const macdSeries = chartRef.current.addSeries(LineSeries, {
          color: '#6C8CFF',
          lineWidth: 2,
          title: 'MACD',
          priceScaleId: 'macd',
          lastValueVisible: false,
          priceLineVisible: false,
        });
        macdSeries.setData(lineData(macd.macdLine));
        indicatorsRef.current[`macd_${index}`] = macdSeries;

        const signalSeries = chartRef.current.addSeries(LineSeries, {
          color: '#E8B84A',
          lineWidth: 2,
          title: 'Signal',
          priceScaleId: 'macd',
          lastValueVisible: false,
          priceLineVisible: false,
        });
        signalSeries.setData(lineData(macd.signalLine));
        indicatorsRef.current[`signal_${index}`] = signalSeries;

        const histSeries = chartRef.current.addSeries(HistogramSeries, {
          priceScaleId: 'macd',
          priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
          lastValueVisible: false,
          priceLineVisible: false,
        });
        histSeries.setData(
          data
            .map((bar, i) => ({
              time: bar.time,
              value: macd.histogram[i],
              color: (macd.histogram[i] ?? 0) >= 0 ? 'rgba(61,220,151,0.55)' : 'rgba(240,113,103,0.55)',
            }))
            .filter((d) => d.value != null)
        );
        indicatorsRef.current[`histogram_${index}`] = histSeries;
        chartRef.current.priceScale('macd').applyOptions({
          scaleMargins: { top: 0.78, bottom: 0 },
        });
        break;
      }
      case 'BB': {
        const bb = calculateBollingerBands(
          data,
          indicator.params.period || 20,
          indicator.params.stdDev || 2
        );
        const upper = chartRef.current.addSeries(LineSeries, {
          color: 'rgba(108,140,255,0.85)',
          lineWidth: 1,
          title: 'BB Upper',
          lastValueVisible: false,
          priceLineVisible: false,
        });
        upper.setData(lineData(bb.upper));
        indicatorsRef.current[`bb_upper_${index}`] = upper;

        const middle = chartRef.current.addSeries(LineSeries, {
          color: 'rgba(139,147,167,0.9)',
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          title: 'BB Mid',
          lastValueVisible: false,
          priceLineVisible: false,
        });
        middle.setData(lineData(bb.middle));
        indicatorsRef.current[`bb_middle_${index}`] = middle;

        const lower = chartRef.current.addSeries(LineSeries, {
          color: 'rgba(108,140,255,0.85)',
          lineWidth: 1,
          title: 'BB Lower',
          lastValueVisible: false,
          priceLineVisible: false,
        });
        lower.setData(lineData(bb.lower));
        indicatorsRef.current[`bb_lower_${index}`] = lower;
        break;
      }
      default:
        break;
    }
  }, []);

  const updateIndicators = useCallback(
    (data) => {
      clearIndicatorSeries();
      indicators.forEach((indicator, index) => addIndicatorToChart(indicator, data, index));
    },
    [addIndicatorToChart, clearIndicatorSeries, indicators]
  );

  const createPriceSeries = useCallback(
    (chart) => {
      if (priceSeriesRef.current) {
        try {
          chart.removeSeries(priceSeriesRef.current);
        } catch {
          /* ignore */
        }
        priceSeriesRef.current = null;
      }

      if (chartType === 'candle') {
        priceSeriesRef.current = chart.addSeries(CandlestickSeries, {
          upColor: '#3DDC97',
          downColor: '#F07167',
          borderVisible: false,
          wickUpColor: '#3DDC97',
          wickDownColor: '#F07167',
        });
      } else if (chartType === 'area') {
        priceSeriesRef.current = chart.addSeries(AreaSeries, {
          lineColor: '#6C8CFF',
          topColor: 'rgba(108, 140, 255, 0.35)',
          bottomColor: 'rgba(108, 140, 255, 0.02)',
          lineWidth: 2,
        });
      } else {
        priceSeriesRef.current = chart.addSeries(LineSeries, {
          color: '#6C8CFF',
          lineWidth: 2,
        });
      }
    },
    [chartType]
  );

  useEffect(() => {
    if (!chartContainerRef.current) return undefined;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#0B0E14' },
        textColor: '#8B93A7',
        fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
        fontSize: 11,
      },
      localization: {
        locale: 'en-US',
        timeFormatter: (time) => formatEtCrosshairTime(time),
      },
      grid: {
        vertLines: { color: 'rgba(255,255,255,0.04)' },
        horzLines: { color: 'rgba(255,255,255,0.04)' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: 'rgba(108,140,255,0.45)',
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: '#1E2330',
        },
        horzLine: {
          color: 'rgba(108,140,255,0.45)',
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: '#1E2330',
        },
      },
      rightPriceScale: {
        borderColor: 'rgba(255,255,255,0.08)',
        scaleMargins: { top: 0.08, bottom: 0.22 },
      },
      timeScale: {
        borderColor: 'rgba(255,255,255,0.08)',
        rightOffset: 8,
        barSpacing: 8,
        minBarSpacing: 2,
        timeVisible: true,
        secondsVisible: timeframe === '1Min',
        tickMarkFormatter: (time, tickMarkType) => formatEtChartTick(time, tickMarkType),
      },
      width: chartContainerRef.current.clientWidth,
      height: chartHeight,
    });

    createPriceSeries(chart);

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
      lastValueVisible: false,
      priceLineVisible: false,
    });
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.82, bottom: 0 },
    });

    chartRef.current = chart;
    volumeSeriesRef.current = volumeSeries;

    chart.subscribeCrosshairMove((param) => {
      // Live fib preview + snap guide while drawing
      if (drawToolRef.current === 'fib' && param?.point && param.time != null) {
        const point = resolvePointRef.current?.(param);
        if (point) {
          fibHoverRef.current = point;
          setFibHoverHint({ snap: point.snap, price: point.price });
          updateSnapPriceLineRef.current?.(point.price, point.snap, point.bar);
          const pending = fibPendingRef.current;
          // Preview as soon as end differs from start (incl. same-bar H↔L)
          if (
            pending &&
            (point.time !== pending.time || Math.abs(point.price - pending.price) > 1e-9)
          ) {
            drawFibOnChartRef.current?.(
              { id: FIB_PREVIEW_ID, p1: pending, p2: point },
              { preview: true }
            );
          } else if (!pending) {
            clearFibPreviewRef.current?.();
          }
        }
      } else if (drawToolRef.current !== 'fib') {
        if (fibHoverRef.current) {
          fibHoverRef.current = null;
          setFibHoverHint(null);
          clearSnapPriceLineRef.current?.();
          clearFibPreviewRef.current?.();
        }
      }

      if (!param?.time || !param.seriesData?.size) {
        const latest = barsRef.current[barsRef.current.length - 1];
        if (latest) {
          setHud({
            open: latest.open,
            high: latest.high,
            low: latest.low,
            close: latest.close,
            volume: latest.volume,
            live: true,
          });
        }
        return;
      }
      const candle = param.seriesData.get(priceSeriesRef.current);
      if (!candle) return;
      const matched = barsRef.current.find((b) => b.time === chartTimeKey(param.time));
      if (chartType === 'candle' && candle.open != null) {
        setHud({
          open: candle.open,
          high: candle.high,
          low: candle.low,
          close: candle.close,
          volume: matched?.volume,
          live: false,
        });
      } else if (candle.value != null) {
        setHud({
          open: matched?.open,
          high: matched?.high,
          low: matched?.low,
          close: candle.value,
          volume: matched?.volume,
          live: false,
        });
      }
    });

    const onChartClick = (param) => {
      if (drawToolRef.current !== 'fib') return;
      const point = resolvePointRef.current?.(param);
      if (!point) return;

      const pending = fibPendingRef.current;
      if (!pending) {
        fibPendingRef.current = point;
        setFibPending(point);
        setFibHoverHint({ snap: point.snap, price: point.price });
        updateSnapPriceLineRef.current?.(point.price, point.snap, point.bar);
        return;
      }
      // Ignore accidental double-click on same price
      if (point.time === pending.time && Math.abs(point.price - pending.price) < 1e-9) {
        return;
      }
      const id = `fib_${Date.now()}`;
      clearFibPreviewRef.current?.();
      clearSnapPriceLineRef.current?.();
      fibPendingRef.current = null;
      setFibDrawings((prev) => [...prev, { id, p1: pending, p2: point }]);
      setFibPending(null);
      setFibHoverHint(null);
    };
    chart.subscribeClick(onChartClick);

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry || !chartRef.current) return;
      chartRef.current.applyOptions({
        width: entry.contentRect.width,
        height: chartHeight,
      });
    });
    ro.observe(chartContainerRef.current);

    if (barsRef.current.length) {
      applyMainSeriesData(barsRef.current);
      volumeSeries.setData(
        barsRef.current.map((bar) => ({
          time: bar.time,
          value: bar.volume,
          color: bar.close >= bar.open ? 'rgba(61,220,151,0.35)' : 'rgba(240,113,103,0.35)',
        }))
      );
      updateIndicators(barsRef.current);
      chart.timeScale().fitContent();
    }

    const onVisibleRange = (range) => {
      if (!range || loadingMoreRef.current || !hasMoreHistoryRef.current) return;
      // Near the left edge of loaded data — pull older bars
      if (range.from > 25) return;
      if (typeof loadOlderBarsRef.current === 'function') {
        loadOlderBarsRef.current();
      }
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(onVisibleRange);

    return () => {
      try {
        chart.timeScale().unsubscribeVisibleLogicalRangeChange(onVisibleRange);
      } catch {
        /* ignore */
      }
      try {
        chart.unsubscribeClick(onChartClick);
      } catch {
        /* ignore */
      }
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      priceSeriesRef.current = null;
      volumeSeriesRef.current = null;
      indicatorsRef.current = {};
      fibSeriesRef.current = {};
      snapPriceLineRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, chartType, chartHeight, createPriceSeries]);

  const loadBars = useCallback(async (opts = {}) => {
    const soft = opts === true || opts?.soft === true;
    if (!symbol) return;
    if (soft) {
      if (softRefreshInFlightRef.current) return;
      softRefreshInFlightRef.current = true;
    }
    try {
      if (!soft) {
        setLoading(true);
        setError(null);
        hasMoreHistoryRef.current = true;
      }
      const visibleRange =
        soft && chartRef.current ? chartRef.current.timeScale().getVisibleLogicalRange() : null;

      const response = await marketDataApi.getBars(symbol, {
        timeframe,
        limit: timeframe === '1Min' || timeframe === '5Min' ? 800 : 500,
      });

      if (!response.success || !response.data) {
        if (!soft) setError('No chart data returned');
        return;
      }

      const formattedBars = formatBars(response.data, timeframe);
      if (!formattedBars.length) {
        if (!soft) {
          setError('No valid bars for this symbol/timeframe');
          setBars([]);
          barsRef.current = [];
        }
        return;
      }

      setBars(formattedBars);
      barsRef.current = formattedBars;
      const latest = formattedBars[formattedBars.length - 1];
      setHud({
        open: latest.open,
        high: latest.high,
        low: latest.low,
        close: latest.close,
        volume: latest.volume,
        live: true,
      });

      if (priceSeriesRef.current) applyMainSeriesData(formattedBars);
      if (volumeSeriesRef.current) {
        volumeSeriesRef.current.setData(
          formattedBars.map((bar) => ({
            time: bar.time,
            value: bar.volume,
            color: bar.close >= bar.open ? 'rgba(61,220,151,0.35)' : 'rgba(240,113,103,0.35)',
          }))
        );
      }
      if (chartRef.current) {
        if (soft && visibleRange) {
          chartRef.current.timeScale().setVisibleLogicalRange(visibleRange);
        } else if (!soft) {
          chartRef.current.timeScale().fitContent();
        }
        chartRef.current.timeScale().applyOptions({
          timeVisible: true,
          secondsVisible: timeframe === '1Min',
        });
      }
      updateIndicators(formattedBars);
    } catch (err) {
      console.error('Error loading bars:', err);
      if (!soft) {
        setError(err.response?.data?.error || err.message || 'Failed to load chart data');
      }
    } finally {
      if (soft) softRefreshInFlightRef.current = false;
      else setLoading(false);
    }
  }, [symbol, timeframe, applyMainSeriesData, updateIndicators]);

  const loadOlderBars = useCallback(async () => {
    if (!symbol || loadingMoreRef.current || !hasMoreHistoryRef.current) return;
    const oldest = barsRef.current[0];
    if (!oldest || !chartRef.current) return;

    const endIso = oldestBarEndIso(oldest);
    if (!endIso) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const page = historyPageSize(timeframe);
      const response = await marketDataApi.getBars(symbol, {
        timeframe,
        limit: page,
        start: historyStartIso(endIso, timeframe),
        end: endIso,
      });
      const raw = response?.data ?? response;
      const older = formatBars(Array.isArray(raw) ? raw : [], timeframe).filter(
        (b) => b.time < oldest.time
      );

      if (!older.length) {
        hasMoreHistoryRef.current = false;
        return;
      }
      // If we got a short page, likely near the start of available history
      if (older.length < Math.floor(page * 0.35)) {
        hasMoreHistoryRef.current = false;
      }

      const logicalRange = chartRef.current.timeScale().getVisibleLogicalRange();
      const merged = [...older, ...barsRef.current].filter(
        (bar, i, arr) => i === 0 || bar.time !== arr[i - 1].time
      );
      barsRef.current = merged;
      setBars(merged);

      applyMainSeriesData(merged);
      if (volumeSeriesRef.current) {
        volumeSeriesRef.current.setData(
          merged.map((bar) => ({
            time: bar.time,
            value: bar.volume,
            color: bar.close >= bar.open ? 'rgba(61,220,151,0.35)' : 'rgba(240,113,103,0.35)',
          }))
        );
      }
      updateIndicators(merged);

      // Keep the same candles under the viewport after prepending
      if (logicalRange) {
        chartRef.current.timeScale().setVisibleLogicalRange({
          from: logicalRange.from + older.length,
          to: logicalRange.to + older.length,
        });
      }
    } catch (err) {
      console.error('Error loading older bars:', err);
      // Don't blank the chart — just stop trying for a bit
      hasMoreHistoryRef.current = false;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [symbol, timeframe, applyMainSeriesData, updateIndicators]);

  useEffect(() => {
    loadOlderBarsRef.current = loadOlderBars;
  }, [loadOlderBars]);

  useEffect(() => {
    loadBars();
  }, [loadBars]);

  // Level 1 quote: REST on symbol change + poll when stream is not live
  useEffect(() => {
    if (!symbol) {
      setL1Quote(null);
      return undefined;
    }
    let cancelled = false;
    setL1Quote(null);

    const fetchQuote = async () => {
      try {
        const res = await marketDataApi.getQuote(symbol);
        if (cancelled) return;
        const raw = res?.data ?? res;
        const next = normalizeL1Quote({ ...raw, source: 'rest' }, symbol);
        if (!next) return;
        setL1Quote((prev) => {
          // Prefer fresher stream quotes over REST while live
          if (prev?.source === 'stream' && streamStatusRef.current === 'live') return prev;
          return next;
        });
      } catch {
        /* ignore — panel shows empty / last */
      }
    };

    fetchQuote();
    const pollMs = autoRefresh && streamStatus === 'live' ? 30000 : 5000;
    const id = setInterval(fetchQuote, pollMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [symbol, autoRefresh, streamStatus]);

  const pushCandleToChart = useCallback((candle) => {
    if (!candle || !priceSeriesRef.current) return;
    if (chartTypeRef.current === 'candle') {
      priceSeriesRef.current.update({
        time: candle.time,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
      });
    } else {
      priceSeriesRef.current.update({ time: candle.time, value: candle.close });
    }
    if (volumeSeriesRef.current) {
      volumeSeriesRef.current.update({
        time: candle.time,
        value: candle.volume,
        color: candle.close >= candle.open ? 'rgba(61,220,151,0.35)' : 'rgba(240,113,103,0.35)',
      });
    }
    setHud({
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
      volume: candle.volume,
      live: true,
    });

    const now = Date.now();
    if (now - barsStateThrottleRef.current > 1500) {
      barsStateThrottleRef.current = now;
      setBars([...barsRef.current]);
    }
    if (now - indicatorThrottleRef.current > 2000) {
      indicatorThrottleRef.current = now;
      updateIndicators(barsRef.current);
    }
  }, [updateIndicators]);

  const applyLiveCandle = useCallback(
    (partial) => {
      if (partial?.time == null || !Number.isFinite(Number(partial.close))) return;
      const bars = barsRef.current;
      if (!bars.length) return;

      const last = bars[bars.length - 1];
      const time = partial.time;
      const newer =
        typeof time === 'string' && typeof last.time === 'string'
          ? time > last.time
          : time > last.time;
      const same = time === last.time;

      let next;
      if (same) {
        if (partial.replace && partial.open != null) {
          next = {
            time,
            open: partial.open,
            high: partial.high,
            low: partial.low,
            close: partial.close,
            volume: partial.volume != null ? partial.volume : last.volume,
          };
        } else {
          const close = Number(partial.close);
          next = {
            ...last,
            high: Math.max(last.high, partial.high != null ? Number(partial.high) : close),
            low: Math.min(last.low, partial.low != null ? Number(partial.low) : close),
            close,
            volume:
              partial.volume != null
                ? Number(partial.volume)
                : last.volume + (Number(partial.volumeDelta) || 0),
          };
        }
        bars[bars.length - 1] = next;
      } else if (newer) {
        const close = Number(partial.close);
        next = {
          time,
          open: partial.open != null ? Number(partial.open) : close,
          high: partial.high != null ? Number(partial.high) : close,
          low: partial.low != null ? Number(partial.low) : close,
          close,
          volume: partial.volume != null ? Number(partial.volume) : Number(partial.volumeDelta) || 0,
        };
        bars.push(next);
      } else {
        return;
      }

      barsRef.current = bars;
      pushCandleToChart(next);
    },
    [pushCandleToChart]
  );

  const handleStreamMessage = useCallback(
    (msg) => {
      if (!msg || typeof msg !== 'object') return;
      const tf = timeframeRef.current;

      if (msg.type === 'subscribed') {
        setStreamStatus('live');
        if (msg.feed) setStreamFeed(msg.feed);
        return;
      }
      if (msg.type === 'status') {
        if (msg.connected === false) setStreamStatus('connecting');
        else if (msg.connected === true) setStreamStatus('live');
        if (msg.feed) setStreamFeed(msg.feed);
        return;
      }
      if (msg.type === 'error') {
        setStreamStatus('error');
        return;
      }
      if (msg.type === 'trade') {
        const time = streamBarTime(msg.timestamp, tf);
        if (time == null) return;
        applyLiveCandle({
          time,
          close: msg.price,
        });
        return;
      }
      if (msg.type === 'quote') {
        const next = normalizeL1Quote({ ...msg, source: 'stream' }, msg.symbol);
        if (next) setL1Quote(next);
        const px = msg.mid ?? msg.ask ?? msg.bid;
        if (px == null || !Number.isFinite(Number(px))) return;
        const time = streamBarTime(msg.timestamp || Date.now(), tf);
        if (time == null) return;
        applyLiveCandle({
          time,
          close: Number(px),
        });
        return;
      }
      if (msg.type === 'bar') {
        const time = streamBarTime(msg.timestamp, tf);
        if (time == null) return;
        if (tf === '1Min' && (msg.kind === 'updated' || msg.kind === 'minute')) {
          applyLiveCandle({
            time,
            open: msg.open,
            high: msg.high,
            low: msg.low,
            close: msg.close,
            volume: msg.volume,
            replace: true,
          });
          return;
        }
        if (!isIntradayTf(tf) && msg.kind === 'daily') {
          applyLiveCandle({
            time,
            open: msg.open,
            high: msg.high,
            low: msg.low,
            close: msg.close,
            volume: msg.volume,
            replace: true,
          });
          return;
        }
        // Higher intraday TFs: fold minute bars into the bucket OHLC (volume from REST/resync).
        applyLiveCandle({
          time,
          high: msg.high,
          low: msg.low,
          close: msg.close,
        });
      }
    },
    [applyLiveCandle]
  );

  // Live mode: Alpaca WebSocket stream (+ light HTTP resync). Poll-only when stream is down.
  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    const closeMarketWs = () => {
      const ws = marketWsRef.current;
      marketWsRef.current = null;
      if (ws) {
        try {
          ws.onopen = null;
          ws.onmessage = null;
          ws.onerror = null;
          ws.onclose = null;
          ws.close();
        } catch {
          /* ignore */
        }
      }
    };

    if (!autoRefresh || !symbol) {
      closeMarketWs();
      setStreamStatus('off');
      return undefined;
    }

    let cancelled = false;
    let reconnectTimer = null;

    const connect = () => {
      if (cancelled) return;
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setStreamStatus('error');
        return;
      }
      closeMarketWs();
      setStreamStatus('connecting');
      const env = localStorage.getItem('tradingEnv') || 'paper';
      const ws = new WebSocket(resolveMarketDataWsUrl(token, env));
      marketWsRef.current = ws;

      ws.onopen = () => {
        if (cancelled || marketWsRef.current !== ws) return;
        try {
          ws.send(
            JSON.stringify({
              type: 'subscribe',
              symbol,
              env,
            })
          );
        } catch {
          setStreamStatus('error');
        }
      };

      ws.onmessage = (event) => {
        if (cancelled || marketWsRef.current !== ws) return;
        try {
          handleStreamMessage(JSON.parse(event.data));
        } catch {
          /* ignore bad frames */
        }
      };

      ws.onerror = () => {
        if (!cancelled) setStreamStatus('error');
      };

      ws.onclose = () => {
        if (marketWsRef.current === ws) marketWsRef.current = null;
        if (cancelled) return;
        setStreamStatus('connecting');
        reconnectTimer = setTimeout(connect, 2500);
      };
    };

    connect();

    // HTTP safety net while Live is on (stream may miss bars / reconnect).
    // When the socket is live, resync less often to cut REST load.
    let pollTicks = 0;
    refreshIntervalRef.current = setInterval(() => {
      pollTicks += 1;
      const live = streamStatusRef.current === 'live';
      const every = live ? Math.max(1, Math.ceil(30 / Math.max(refreshInterval, 1))) : 1;
      if (pollTicks % every !== 0) return;
      loadBars({ soft: true });
    }, refreshInterval * 1000);

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      closeMarketWs();
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      setStreamStatus('off');
    };
  }, [autoRefresh, symbol, refreshInterval, loadBars, handleStreamMessage]);

  const flashOrderMessage = useCallback((msg) => {
    if (orderMsgTimerRef.current) clearTimeout(orderMsgTimerRef.current);
    setOrderMessage(msg);
    orderMsgTimerRef.current = setTimeout(() => setOrderMessage(null), 5000);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!accessToken) return;
      try {
        setSchwabAccountsLoading(true);
        const statusRes = await schwabApi.getStatus().catch(() => ({ connected: false }));
        if (cancelled) return;
        const connected = Boolean(statusRes?.connected || statusRes?.data?.connected);
        setSchwabConnected(connected);
        if (!connected) {
          setSchwabAccounts([]);
          return;
        }
        const acctRes = await schwabApi.getAccounts(false);
        if (cancelled) return;
        const list = acctRes?.data || acctRes?.accounts || [];
        const accounts = Array.isArray(list) ? list : [];
        setSchwabAccounts(accounts);
        if (
          accounts.length &&
          !accounts.some((a) => String(a.accountNumber) === String(schwabAccountNumber))
        ) {
          const first = String(accounts[0].accountNumber || '');
          setSchwabAccountNumber(first);
          try {
            localStorage.setItem(SCHWAB_ACCOUNT_STORAGE_KEY, first);
          } catch {
            /* ignore */
          }
        }
      } catch {
        if (!cancelled) {
          setSchwabConnected(false);
          setSchwabAccounts([]);
        }
      } finally {
        if (!cancelled) setSchwabAccountsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, executionVenue]);

  const loadPosition = useCallback(async () => {
    if (!symbol) {
      setPosition(null);
      return;
    }
    try {
      if (executionVenue === 'schwab') {
        if (!schwabConnected) {
          setPosition(null);
          return;
        }
        const response = await schwabApi.getPositions();
        const list = response.data || [];
        const match = list.find((p) => {
          const sameSym = String(p.symbol || '').toUpperCase() === symbol.toUpperCase();
          if (!sameSym) return false;
          if (!schwabAccountNumber) return true;
          return String(p.accountNumber) === String(schwabAccountNumber);
        });
        setPosition(match || null);
        return;
      }
      const response = await accountApi.getPositions();
      const list = response.data || [];
      const match = list.find((p) => String(p.symbol || '').toUpperCase() === symbol.toUpperCase());
      setPosition(match || null);
    } catch (err) {
      console.error('Failed to load chart position:', err);
      setPosition(null);
    }
  }, [symbol, executionVenue, schwabConnected, schwabAccountNumber]);

  useEffect(() => {
    loadPosition();
  }, [loadPosition]);

  const loadDayFills = useCallback(async () => {
    if (!symbol) {
      setDayFills([]);
      return;
    }
    const sym = symbol.toUpperCase();
    const today = etTodayYmd();
    try {
      setDayFillsLoading(true);
      let next = [];
      if (executionVenue === 'schwab') {
        if (!schwabConnected) {
          setDayFills([]);
          return;
        }
        try {
          const res = await schwabApi.getTransactions({
            types: 'TRADE',
            days: 1,
            symbol: sym,
            accountNumber: schwabAccountNumber || undefined,
          });
          const list = res?.data || res?.transactions || [];
          next = (Array.isArray(list) ? list : [])
            .map((t) => normalizeSchwabTrade(t, sym))
            .filter(Boolean)
            .filter((f) => f.symbol === sym);
        } catch {
          const res = await schwabApi.getOrders({ days: 1, status: 'FILLED' });
          const list = res?.data || res?.orders || [];
          next = (Array.isArray(list) ? list : [])
            .map((o) => normalizeSchwabFilledOrder(o, sym))
            .filter(Boolean)
            .filter((f) => f.symbol === sym);
        }
      } else {
        const res = await accountApi.getActivities('FILL', {
          date: today,
          direction: 'desc',
          page_size: 100,
        });
        const list = res?.data || [];
        next = (Array.isArray(list) ? list : [])
          .map((a) => normalizeAlpacaFill(a, sym))
          .filter(Boolean)
          .filter((f) => f.symbol === sym);
      }
      // newest first already; keep stable sort by ts desc
      const todayStart = (() => {
        // rough: keep fills whose ET calendar day matches today
        return today;
      })();
      next = next.filter((f) => {
        if (!f.ts) return true;
        try {
          const parts = Object.fromEntries(
            new Intl.DateTimeFormat('en-CA', {
              timeZone: 'America/New_York',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })
              .formatToParts(new Date(f.ts))
              .filter((p) => p.type !== 'literal')
              .map((p) => [p.type, p.value])
          );
          return `${parts.year}-${parts.month}-${parts.day}` === todayStart;
        } catch {
          return true;
        }
      });
      next.sort((a, b) => (b.ts || 0) - (a.ts || 0));
      setDayFills(next);
    } catch (err) {
      console.error('Failed to load day fills:', err);
    } finally {
      setDayFillsLoading(false);
    }
  }, [symbol, executionVenue, schwabConnected, schwabAccountNumber]);

  useEffect(() => {
    loadDayFills();
  }, [loadDayFills]);

  useEffect(() => {
    try {
      localStorage.setItem(DAY_TAPE_OPEN_KEY, dayTapeOpen ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [dayTapeOpen]);

  const daySummary = useMemo(() => summarizeDayFills(dayFills), [dayFills]);

  const intradayPl = useMemo(() => {
    if (!position) return null;
    const v = Number(position.unrealizedIntradayPl ?? position.unrealized_intraday_pl);
    return Number.isFinite(v) ? v : null;
  }, [position]);

  const refreshDayFillsSoon = useCallback(() => {
    // Broker fill ledger can lag market ack by a second or two
    setTimeout(() => {
      loadDayFills();
    }, 1500);
    setTimeout(() => {
      loadDayFills();
    }, 4000);
  }, [loadDayFills]);

  useEffect(() => {
    try {
      const n = Number(orderQty);
      if (Number.isFinite(n) && n > 0) localStorage.setItem(CHART_QTY_KEY, String(n));
    } catch {
      /* ignore */
    }
  }, [orderQty]);

  useEffect(
    () => () => {
      if (orderMsgTimerRef.current) clearTimeout(orderMsgTimerRef.current);
    },
    []
  );

  const refMid = useCallback(() => {
    const mid = Number(l1Quote?.mid);
    if (Number.isFinite(mid) && mid > 0) return mid;
    const last = barsRef.current[barsRef.current.length - 1];
    const close = Number(last?.close);
    return Number.isFinite(close) && close > 0 ? close : null;
  }, [l1Quote]);

  const snapChartPrices = useCallback(() => {
    const mid = refMid();
    if (mid == null) {
      flashOrderMessage({ type: 'error', text: 'No mid/last to snap from' });
      return;
    }
    setLimitPrice(mid.toFixed(2));
    setTpPrice((mid * 1.01).toFixed(2));
    setSlPrice((mid * 0.99).toFixed(2));
    if (!alertThreshold) setAlertThreshold(mid.toFixed(2));
  }, [refMid, flashOrderMessage, alertThreshold]);

  useEffect(() => {
    // Seed empty price fields when symbol changes
    setLimitPrice('');
    setTpPrice('');
    setSlPrice('');
    setAlertThreshold('');
  }, [symbol]);

  useEffect(() => {
    if (tradeMode === 'mkt' || tradeMode === 'alert') return;
    const mid = refMid();
    if (mid == null) return;
    if (tradeMode === 'lmt' && !limitPrice) setLimitPrice(mid.toFixed(2));
    if ((tradeMode === 'brkt' || tradeMode === 'tp') && !tpPrice) {
      setTpPrice((mid * 1.01).toFixed(2));
    }
    if (tradeMode === 'brkt' && !slPrice) setSlPrice((mid * 0.99).toFixed(2));
    if (tradeMode === 'alert' && !alertThreshold) setAlertThreshold(mid.toFixed(2));
  }, [tradeMode, refMid, limitPrice, tpPrice, slPrice, alertThreshold]);

  const setChartAlert = useCallback(async () => {
    const created = await addAlert({
      symbol,
      type: alertType,
      threshold: parseFloat(alertThreshold),
    });
    if (!created) {
      flashOrderMessage({ type: 'error', text: 'Enter a valid alert threshold' });
      return;
    }
    flashOrderMessage({
      type: 'success',
      text: `Alert set: ${created.symbol} ${created.type} ${created.threshold}`,
    });
  }, [addAlert, symbol, alertType, alertThreshold, flashOrderMessage]);

  const submitChartOrder = useCallback(
    async (side) => {
      const qty = Number(orderQty);
      if (!symbol || !Number.isFinite(qty) || qty <= 0) {
        flashOrderMessage({ type: 'error', text: 'Enter a valid share size' });
        return;
      }

      const mode = tradeMode;
      const lim = Number(limitPrice);
      const tp = Number(tpPrice);
      const sl = Number(slPrice);
      const posQty = position ? Math.abs(Number(position.qty) || 0) : 0;
      const isLong = position && Number(position.qty) > 0;

      try {
        setOrderBusy(true);

        if (mode === 'tp') {
          if (!Number.isFinite(tp) || tp <= 0) {
            flashOrderMessage({ type: 'error', text: 'Enter a take-profit price' });
            return;
          }
          const orderData = {
            symbol: symbol.toUpperCase(),
            qty,
            side: 'sell',
            type: 'limit',
            limitPrice: tp,
            timeInForce: 'day',
          };
          const estimatedNotional = qty * tp;
          if (executionVenue === 'alpaca') {
            const risk = await checkOrder({ ...orderData, estimatedNotional });
            if (risk?.blocks?.length) {
              flashOrderMessage({ type: 'error', text: `Risk block: ${risk.blocks.join(' · ')}` });
              return;
            }
            if (risk?.warnings?.length) {
              const ok = window.confirm(`${risk.warnings.join('\n')}\n\nTP sell ${qty} ${symbol} @ ${tp}?`);
              if (!ok) return;
            }
            await orderApi.submitOrder(orderData);
          } else {
            if (!schwabConnected || !schwabAccountNumber) throw new Error('Select a connected Schwab account');
            await schwabApi.placeOrder(mapAlpacaToSchwabPayload(orderData, schwabAccountNumber));
          }
          flashOrderMessage({ type: 'success', text: `TP sell ${qty} ${symbol} @ ${tp}` });
          await loadPosition();
          refreshDayFillsSoon();
          return;
        }

        if (mode === 'brkt') {
          if (executionVenue !== 'alpaca') {
            flashOrderMessage({ type: 'error', text: 'Bracket / OCO requires Alpaca' });
            return;
          }
          if (!Number.isFinite(tp) || tp <= 0 || !Number.isFinite(sl) || sl <= 0) {
            flashOrderMessage({ type: 'error', text: 'Enter TP and SL prices' });
            return;
          }

          if (side === 'sell' && isLong && posQty > 0) {
            // Exit long with OCO (take-profit + stop)
            const exitQty = Math.min(qty, posQty) || posQty;
            const ok = window.confirm(
              `OCO exit ${exitQty} ${symbol}?\nTP limit ${tp}\nSL stop ${sl}`
            );
            if (!ok) return;
            await orderApi.submitOtoOrder({
              symbol: symbol.toUpperCase(),
              qty: exitQty,
              side: 'sell',
              type: 'limit',
              limitPrice: tp,
              timeInForce: 'gtc',
              takeProfitLimitPrice: tp,
              stopLossStopPrice: sl,
              orderClass: 'oco',
            });
            flashOrderMessage({ type: 'success', text: `OCO exit ${exitQty} ${symbol} submitted` });
          } else {
            // Entry bracket
            if (side === 'buy' && tp <= sl) {
              flashOrderMessage({ type: 'error', text: 'Long bracket needs TP > SL' });
              return;
            }
            if (side === 'sell' && tp >= sl) {
              flashOrderMessage({ type: 'error', text: 'Short bracket needs TP < SL' });
              return;
            }
            const mid = refMid();
            const estimatedNotional = mid ? qty * mid : null;
            const risk = await checkOrder({
              symbol: symbol.toUpperCase(),
              qty,
              side,
              type: 'market',
              estimatedNotional,
            });
            if (risk?.blocks?.length) {
              flashOrderMessage({ type: 'error', text: `Risk block: ${risk.blocks.join(' · ')}` });
              return;
            }
            if (risk?.warnings?.length) {
              const ok = window.confirm(
                `${risk.warnings.join('\n')}\n\nBracket ${side.toUpperCase()} ${qty} ${symbol}?\nTP ${tp} · SL ${sl}`
              );
              if (!ok) return;
            }
            await orderApi.submitOtoOrder({
              symbol: symbol.toUpperCase(),
              qty,
              side,
              type: 'market',
              timeInForce: 'gtc',
              takeProfitLimitPrice: tp,
              stopLossStopPrice: sl,
              orderClass: 'bracket',
            });
            flashOrderMessage({
              type: 'success',
              text: `Bracket ${side.toUpperCase()} ${qty} ${symbol} · TP ${tp} / SL ${sl}`,
            });
          }
          await loadPosition();
          refreshDayFillsSoon();
          return;
        }

        const orderData = {
          symbol: symbol.toUpperCase(),
          qty,
          side,
          type: mode === 'lmt' ? 'limit' : 'market',
          timeInForce: 'day',
        };
        if (mode === 'lmt') {
          if (!Number.isFinite(lim) || lim <= 0) {
            flashOrderMessage({ type: 'error', text: 'Enter a limit price' });
            return;
          }
          orderData.limitPrice = lim;
        }

        const last = barsRef.current[barsRef.current.length - 1];
        const estimatedNotional =
          mode === 'lmt' ? qty * lim : last?.close ? qty * Number(last.close) : null;

        if (executionVenue === 'alpaca') {
          const risk = await checkOrder({ ...orderData, estimatedNotional });
          if (risk?.blocks?.length) {
            flashOrderMessage({ type: 'error', text: `Risk block: ${risk.blocks.join(' · ')}` });
            return;
          }
          if (risk?.warnings?.length) {
            const ok = window.confirm(
              `${risk.warnings.join('\n')}\n\nSubmit ${orderData.type} ${side.toUpperCase()} ${qty} ${symbol}?`
            );
            if (!ok) return;
          }
          await orderApi.submitOrder(orderData);
          flashOrderMessage({
            type: 'success',
            text: `Alpaca ${orderData.type} ${side.toUpperCase()} ${qty} ${symbol}${
              mode === 'lmt' ? ` @ ${lim}` : ''
            }`,
          });
        } else {
          if (!schwabConnected) throw new Error('Connect Schwab on the Schwab page first');
          if (!schwabAccountNumber) throw new Error('Select a Schwab account');
          await schwabApi.placeOrder(mapAlpacaToSchwabPayload(orderData, schwabAccountNumber));
          flashOrderMessage({
            type: 'success',
            text: `Schwab ${orderData.type} ${side.toUpperCase()} ${qty} ${symbol}${
              mode === 'lmt' ? ` @ ${lim}` : ''
            }`,
          });
        }
        await loadPosition();
        refreshDayFillsSoon();
      } catch (err) {
        flashOrderMessage({
          type: 'error',
          text: err.response?.data?.error || err.message || 'Order failed',
        });
      } finally {
        setOrderBusy(false);
      }
    },
    [
      orderQty,
      symbol,
      tradeMode,
      limitPrice,
      tpPrice,
      slPrice,
      position,
      checkOrder,
      flashOrderMessage,
      loadPosition,
      refreshDayFillsSoon,
      executionVenue,
      schwabConnected,
      schwabAccountNumber,
      refMid,
    ]
  );

  const flattenPosition = useCallback(async () => {
    if (!symbol || !position) {
      flashOrderMessage({
        type: 'error',
        text: `No open ${executionVenue === 'schwab' ? 'Schwab' : 'Alpaca'} position in ${symbol || '—'}`,
      });
      return;
    }
    const qtyAbs = Math.abs(Number(position.qty) || 0);
    const side = position.side || (Number(position.qty) >= 0 ? 'long' : 'short');
    const venueLabel =
      executionVenue === 'schwab'
        ? `Schwab ${maskAccount(schwabAccountNumber || position.accountNumber)}`
        : 'Alpaca';
    const ok = window.confirm(
      `Flatten ${symbol} on ${venueLabel}?\n\nMarket order will close the entire ${side} position (${qtyAbs} shares).`
    );
    if (!ok) return;
    try {
      setFlattenBusy(true);
      if (executionVenue === 'schwab') {
        const accountNumber = schwabAccountNumber || position.accountNumber;
        if (!accountNumber) throw new Error('Select a Schwab account');
        const instruction = side === 'short' ? 'BUY_TO_COVER' : 'SELL';
        await schwabApi.placeOrder({
          accountNumber: String(accountNumber),
          symbol: symbol.toUpperCase(),
          instruction,
          quantity: qtyAbs,
          orderType: 'MARKET',
          duration: 'DAY',
          session: 'NORMAL',
          assetType: position.assetType || 'EQUITY',
        });
      } else {
        await accountApi.closePosition(symbol);
      }
      flashOrderMessage({
        type: 'success',
        text: `${symbol} flattened on ${venueLabel}`,
      });
      await loadPosition();
      refreshDayFillsSoon();
    } catch (err) {
      flashOrderMessage({
        type: 'error',
        text: err.response?.data?.error || err.message || 'Flatten failed',
      });
    } finally {
      setFlattenBusy(false);
    }
  }, [
    symbol,
    position,
    flashOrderMessage,
    loadPosition,
    refreshDayFillsSoon,
    executionVenue,
    schwabAccountNumber,
  ]);

  useEffect(() => {
    if (barsRef.current.length) updateIndicators(barsRef.current);
  }, [indicators, updateIndicators]);

  // Re-draw fibs after chart recreate / data / drawing changes
  useEffect(() => {
    if (!chartRef.current || !priceSeriesRef.current) return;
    // Defer so chart series exist after recreate
    const t = requestAnimationFrame(() => renderAllFibs());
    return () => cancelAnimationFrame(t);
  }, [fibDrawings, renderAllFibs, timeframe, chartType, chartHeight, bars]);

  // Clear drawings when symbol changes
  useEffect(() => {
    fibPendingRef.current = null;
    setFibDrawings([]);
    setFibPending(null);
    setDrawTool(null);
    setFibHoverHint(null);
    clearFibPreview();
    clearSnapPriceLine();
  }, [symbol, clearFibPreview, clearSnapPriceLine]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      fibPendingRef.current = null;
      setFibPending(null);
      setDrawTool(null);
      setFibHoverHint(null);
      clearFibPreview();
      clearSnapPriceLine();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clearFibPreview, clearSnapPriceLine]);

  const commitSymbol = () => {
    const next = symbolInput.trim().toUpperCase();
    if (next) setSymbol(next);
  };

  const handleAddIndicator = () => {
    const newIndicator = {
      id: Date.now(),
      type: selectedIndicator,
      params: { ...indicatorParams },
      color: INDICATOR_COLORS[indicators.length % INDICATOR_COLORS.length],
    };
    setIndicators((prev) => [...prev, newIndicator]);
    setShowIndicatorMenu(false);
    setIndicatorParams(defaultParams(selectedIndicator));
  };

  const handleRemoveIndicator = (id) => {
    setIndicators((prev) => prev.filter((ind) => ind.id !== id));
  };

  const toggleFibTool = () => {
    setShowIndicatorMenu(false);
    fibPendingRef.current = null;
    setFibPending(null);
    setFibHoverHint(null);
    clearFibPreview();
    clearSnapPriceLine();
    setDrawTool((prev) => (prev === 'fib' ? null : 'fib'));
  };

  const removeFib = (id) => {
    setFibDrawings((prev) => prev.filter((f) => f.id !== id));
  };

  const adjustFib = (id) => {
    const fib = fibDrawings.find((f) => f.id === id);
    if (!fib) return;
    setFibDrawings((prev) => prev.filter((f) => f.id !== id));
    fibPendingRef.current = fib.p1;
    setFibPending(fib.p1);
    setDrawTool('fib');
    setFibHoverHint({ snap: fib.p1.snap, price: fib.p1.price });
    const bar = barsRef.current.find((b) => b.time === fib.p1.time);
    updateSnapPriceLine(fib.p1.price, fib.p1.snap, bar);
  };

  const clearAllFibs = () => {
    fibPendingRef.current = null;
    setFibDrawings([]);
    setFibPending(null);
    setFibHoverHint(null);
    clearFibPreview();
    clearSnapPriceLine();
  };

  const fitChart = () => {
    chartRef.current?.timeScale().fitContent();
  };

  const display = hud || lastBar;

  return (
    <main className={`flex-1 ${fullscreen ? 'px-2 py-2' : 'container mx-auto px-4 lg:px-6 py-3'}`}>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[var(--lt-text)] flex items-center tracking-tight">
            <FontAwesomeIcon icon={faChartLine} className="mr-2 text-[var(--lt-action)]" />
            Advanced Charting
          </h1>
          <p className="text-xs text-[var(--lt-muted)] mt-1">
            Desk chart · JetBrains Mono readouts · US/Eastern session dates on daily bars
          </p>
        </div>
        <button
          type="button"
          onClick={() => setFullscreen((v) => !v)}
          className="px-2.5 py-1.5 text-xs rounded border border-[var(--lt-border)] text-[var(--lt-muted)] hover:bg-[var(--lt-hover)]"
          title={fullscreen ? 'Exit wide layout' : 'Widen chart'}
        >
          <FontAwesomeIcon icon={fullscreen ? faCompress : faExpand} className="mr-1.5" />
          {fullscreen ? 'Compact' : 'Wide'}
        </button>
      </div>

      <div className="rounded-xl border border-[var(--lt-border)] bg-[var(--lt-surface)] overflow-hidden">
        <div className="px-3 py-2.5 border-b border-[var(--lt-border)] flex flex-wrap items-center gap-2">
          <form
            className="flex items-center gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              commitSymbol();
            }}
          >
            <input
              value={symbolInput}
              onChange={(e) => setSymbolInput(e.target.value.toUpperCase())}
              onBlur={commitSymbol}
              className="w-24 px-2 py-1.5 rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-sm font-semibold tracking-wide text-[var(--lt-text)] focus:outline-none focus:border-[var(--lt-action)]"
              placeholder="SYM"
              spellCheck={false}
            />
            <button
              type="submit"
              className="px-2 py-1.5 text-xs rounded-md bg-[var(--lt-action-dim)] text-[var(--lt-action)]"
            >
              Go
            </button>
          </form>

          <div className="h-5 w-px bg-[var(--lt-border)] mx-1 hidden sm:block" />

          <div className="flex flex-wrap gap-1">
            {TIMEFRAMES.map((tf) => (
              <button
                key={tf.value}
                type="button"
                onClick={() => setTimeframe(tf.value)}
                className={`px-2 py-1 rounded text-[11px] font-medium font-mono transition-colors ${
                  timeframe === tf.value
                    ? 'bg-[var(--lt-action)] text-white'
                    : 'text-[var(--lt-muted)] hover:bg-[var(--lt-hover)] hover:text-[var(--lt-text)]'
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>

          <div className="h-5 w-px bg-[var(--lt-border)] mx-1 hidden sm:block" />

          <div className="flex gap-1">
            {[
              { id: 'candle', label: 'Candle' },
              { id: 'line', label: 'Line' },
              { id: 'area', label: 'Area' },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setChartType(t.id)}
                className={`px-2 py-1 rounded text-[11px] font-medium ${
                  chartType === t.id
                    ? 'bg-[var(--lt-hover)] text-[var(--lt-text)] border border-[var(--lt-border-strong)]'
                    : 'text-[var(--lt-muted)] hover:bg-[var(--lt-hover)]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex-1" />

          <button
            type="button"
            onClick={fitChart}
            className="px-2 py-1.5 text-xs rounded-md text-[var(--lt-muted)] hover:bg-[var(--lt-hover)]"
          >
            Fit
          </button>
          <button
            type="button"
            onClick={() => loadBars()}
            disabled={loading}
            className="px-2.5 py-1.5 text-xs rounded-md bg-[var(--lt-action)] text-white disabled:opacity-50"
            title="Reload bars"
          >
            <FontAwesomeIcon icon={loading ? faSpinner : faSync} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            onClick={() => setAutoRefresh((v) => !v)}
            className={`px-2.5 py-1.5 text-xs rounded-md flex items-center gap-1.5 ${
              autoRefresh
                ? streamStatus === 'live'
                  ? 'bg-[var(--lt-bull-dim)] text-[var(--lt-bull)]'
                  : streamStatus === 'error'
                    ? 'bg-[rgba(240,113,103,0.15)] text-[var(--lt-bear)]'
                    : 'bg-[var(--lt-action-dim)] text-[var(--lt-action)]'
                : 'bg-[var(--lt-hover)] text-[var(--lt-muted)]'
            }`}
            title="Live via Alpaca WebSocket (IEX) with HTTP resync"
          >
            <FontAwesomeIcon icon={autoRefresh ? faPause : faPlay} />
            {!autoRefresh
              ? 'Auto'
              : streamStatus === 'live'
                ? 'Stream'
                : streamStatus === 'connecting'
                  ? 'Connecting…'
                  : streamStatus === 'error'
                    ? 'Stream err'
                    : 'Live'}
          </button>
          <select
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(Number(e.target.value))}
            className="px-2 py-1.5 text-xs rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-[var(--lt-text)]"
            title={autoRefresh ? 'HTTP resync interval (stream is primary)' : 'Polling interval'}
          >
            {LIVE_INTERVALS.map((s) => (
              <option key={s} value={s}>
                {s}s
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={toggleFibTool}
            className={`px-2.5 py-1.5 text-xs rounded-md font-semibold ${
              drawTool === 'fib'
                ? 'bg-[#E8B84A] text-[#0B0E14]'
                : 'bg-[var(--lt-hover)] text-[var(--lt-muted)] hover:text-[var(--lt-text)]'
            }`}
            title="Fibonacci retracement — click two points"
          >
            Fib
          </button>
          <button
            type="button"
            onClick={() => setShowIndicatorMenu((v) => !v)}
            className="px-2.5 py-1.5 text-xs rounded-md bg-[var(--lt-bull)] text-[#0B0E14] font-semibold"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            Study
          </button>
        </div>

        <div className="px-3 py-2 border-b border-[var(--lt-border)] bg-[#0E131B] flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-[var(--lt-faint)] font-semibold mr-1">
            Trade
          </span>
          <label className="flex items-center gap-1.5 text-[11px] text-[var(--lt-muted)]">
            Account
            <select
              value={executionVenue}
              onChange={(e) => {
                const next = e.target.value === 'schwab' ? 'schwab' : 'alpaca';
                setExecutionVenue(next);
                try {
                  localStorage.setItem(VENUE_STORAGE_KEY, next);
                } catch {
                  /* ignore */
                }
              }}
              className="h-8 px-2 rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-[var(--lt-text)] text-xs min-w-[100px]"
              title="Where Buy / Sell / Flatten are sent"
            >
              <option value="alpaca">Alpaca</option>
              <option value="schwab">Schwab</option>
            </select>
          </label>
          {executionVenue === 'schwab' && (
            <select
              value={schwabAccountNumber}
              onChange={(e) => {
                const next = e.target.value;
                setSchwabAccountNumber(next);
                try {
                  localStorage.setItem(SCHWAB_ACCOUNT_STORAGE_KEY, next);
                } catch {
                  /* ignore */
                }
              }}
              disabled={schwabAccountsLoading || !schwabConnected || schwabAccounts.length === 0}
              className="h-8 px-2 rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-[var(--lt-text)] text-xs min-w-[130px]"
              title={
                !schwabConnected
                  ? 'Connect Schwab on the Schwab page first'
                  : 'Schwab account for chart trades'
              }
            >
              {!schwabConnected && <option value="">Not connected</option>}
              {schwabConnected && schwabAccounts.length === 0 && (
                <option value="">{schwabAccountsLoading ? 'Loading…' : 'No accounts'}</option>
              )}
              {schwabAccounts.map((a) => (
                <option key={a.accountNumber} value={String(a.accountNumber)}>
                  {a.accountName || a.nickName || maskAccount(a.accountNumber)}
                </option>
              ))}
            </select>
          )}
          {executionVenue === 'schwab' && !schwabConnected ? (
            <span className="text-[10px] text-[#6B7289]">Connect Schwab to trade</span>
          ) : null}
          {orderMessage ? (
            <span
              className={`ml-auto text-[11px] ${
                orderMessage.type === 'success' ? 'text-[#3DDC97]' : 'text-[#F07167]'
              }`}
            >
              {orderMessage.text}
            </span>
          ) : autoRefresh ? (
            <span
              className={`ml-auto text-[10px] uppercase tracking-wider ${
                streamStatus === 'live'
                  ? 'text-[#3DDC97]'
                  : streamStatus === 'error'
                    ? 'text-[#F07167]'
                    : 'text-[#6C8CFF]'
              }`}
            >
              {streamStatus === 'live'
                ? `Stream · ${streamFeed || 'iex'}`
                : streamStatus === 'connecting'
                  ? 'Stream connecting…'
                  : streamStatus === 'error'
                    ? `Poll fallback · ${refreshInterval}s`
                    : `Live · ${refreshInterval}s`}
            </span>
          ) : null}
        </div>

        {drawTool === 'fib' || fibDrawings.length > 0 ? (
          <div className="px-3 py-1.5 border-b border-[var(--lt-border)] bg-[#10141C] flex flex-wrap items-center gap-2 text-[11px]">
            {drawTool === 'fib' ? (
              <span className="text-[#E8B84A]">
                {fibPending
                  ? 'Blue = start · green High / red Low guides · click end · Esc cancel'
                  : 'Green High + red Low guides · click a wick to start · Esc cancel'}
                {fibHoverHint?.price != null ? (
                  <span className="ml-2 text-[var(--lt-muted)] font-mono">
                    {fibHoverHint.snap ? `${fibHoverHint.snap} ` : ''}
                    {formatPrice(fibHoverHint.price)}
                  </span>
                ) : null}
              </span>
            ) : (
              <span className="text-[var(--lt-muted)]">
                {fibDrawings.length} Fibonacci drawing{fibDrawings.length === 1 ? '' : 's'}
              </span>
            )}
            <label className="inline-flex items-center gap-1 text-[var(--lt-muted)]">
              Snap
              <select
                value={fibSnapMode}
                onChange={(e) => setFibSnapMode(e.target.value)}
                className="h-6 rounded border border-[var(--lt-border)] bg-[var(--lt-input)] px-1 text-[10px] text-[var(--lt-text)]"
              >
                <option value="hl">High / Low</option>
                <option value="ohlc">OHLC</option>
                <option value="free">Free</option>
              </select>
            </label>
            <div className="flex flex-wrap gap-1 ml-auto">
              {fibDrawings.map((fib, i) => (
                <span
                  key={fib.id}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border border-[var(--lt-border)] text-[var(--lt-muted)]"
                >
                  Fib {i + 1}
                  <button
                    type="button"
                    onClick={() => adjustFib(fib.id)}
                    className="text-[var(--lt-action)] hover:underline"
                    title="Keep start, re-pick end with live preview"
                  >
                    Adjust
                  </button>
                  <button
                    type="button"
                    onClick={() => removeFib(fib.id)}
                    className="text-[var(--lt-faint)] hover:text-[var(--lt-bear)]"
                    title="Remove"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </span>
              ))}
              {fibDrawings.length > 0 ? (
                <button
                  type="button"
                  onClick={clearAllFibs}
                  className="px-1.5 py-0.5 rounded text-[10px] text-[var(--lt-faint)] hover:text-[var(--lt-bear)]"
                >
                  Clear all
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="px-3 py-2 border-b border-white/10 flex flex-wrap items-baseline gap-x-4 gap-y-1 bg-[#10141C]">
          <div className="flex items-baseline gap-2 min-w-[140px]">
            <span className="text-lg font-semibold tracking-wide text-[#E8EDF7]">{symbol}</span>
            <span className="text-[10px] uppercase tracking-wider text-[#6B7289]" title="Chart times in America/New_York">
              ET
            </span>
            <span
              className={`font-mono text-xl tabular-nums ${
                bullish ? 'text-[#3DDC97]' : 'text-[#F07167]'
              }`}
            >
              {formatPrice(display?.close ?? lastBar?.close)}
            </span>
            <span
              className={`font-mono text-xs tabular-nums ${
                bullish ? 'text-[#3DDC97]' : 'text-[#F07167]'
              }`}
            >
              {formatPct(changePct)}
            </span>
          </div>

          <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11px] text-[#8B93A7] tabular-nums">
            <span>
              O <b className="text-[#E8EDF7] font-medium">{formatPrice(display?.open)}</b>
            </span>
            <span>
              H <b className="text-[#3DDC97] font-medium">{formatPrice(display?.high)}</b>
            </span>
            <span>
              L <b className="text-[#F07167] font-medium">{formatPrice(display?.low)}</b>
            </span>
            <span>
              C <b className="text-[#E8EDF7] font-medium">{formatPrice(display?.close)}</b>
            </span>
            <span>
              Vol <b className="text-[#E8EDF7] font-medium">{formatVolume(display?.volume)}</b>
            </span>
            {hud && !hud.live && (
              <span className="text-[#6B7289] uppercase tracking-wider">crosshair</span>
            )}
          </div>

          {indicators.length > 0 && (
            <div className="flex flex-wrap gap-1 ml-auto">
              {indicators.map((ind) => (
                <span
                  key={ind.id}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border border-[var(--lt-border)] text-[var(--lt-muted)]"
                  style={{ borderLeftColor: ind.color, borderLeftWidth: 2 }}
                >
                  {ind.type}
                  {ind.params.period ? `(${ind.params.period})` : ''}
                  {ind.params.fastPeriod
                    ? `(${ind.params.fastPeriod},${ind.params.slowPeriod},${ind.params.signalPeriod})`
                    : ''}
                  <button
                    type="button"
                    onClick={() => handleRemoveIndicator(ind.id)}
                    className="text-[var(--lt-faint)] hover:text-[var(--lt-bear)]"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {showIndicatorMenu && (
          <div className="px-3 py-2.5 border-b border-[var(--lt-border)] bg-[var(--lt-hover)] flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
                Study
              </label>
              <select
                value={selectedIndicator}
                onChange={(e) => {
                  setSelectedIndicator(e.target.value);
                  setIndicatorParams(defaultParams(e.target.value));
                }}
                className="px-2 py-1.5 text-xs rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-[var(--lt-text)]"
              >
                {INDICATOR_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            {(selectedIndicator === 'SMA' ||
              selectedIndicator === 'EMA' ||
              selectedIndicator === 'RSI' ||
              selectedIndicator === 'BB') && (
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
                  Period
                </label>
                <input
                  type="number"
                  min="1"
                  max="200"
                  value={indicatorParams.period || 20}
                  onChange={(e) =>
                    setIndicatorParams({
                      ...indicatorParams,
                      period: parseInt(e.target.value, 10) || 20,
                    })
                  }
                  className="w-20 px-2 py-1.5 text-xs rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-[var(--lt-text)]"
                />
              </div>
            )}

            {selectedIndicator === 'MACD' && (
              <>
                {['fastPeriod', 'slowPeriod', 'signalPeriod'].map((key, i) => (
                  <div key={key}>
                    <label className="block text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
                      {['Fast', 'Slow', 'Signal'][i]}
                    </label>
                    <input
                      type="number"
                      value={indicatorParams[key] || [12, 26, 9][i]}
                      onChange={(e) =>
                        setIndicatorParams({
                          ...indicatorParams,
                          [key]: parseInt(e.target.value, 10) || [12, 26, 9][i],
                        })
                      }
                      className="w-16 px-2 py-1.5 text-xs rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-[var(--lt-text)]"
                    />
                  </div>
                ))}
              </>
            )}

            {selectedIndicator === 'BB' && (
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
                  Std Dev
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={indicatorParams.stdDev || 2}
                  onChange={(e) =>
                    setIndicatorParams({
                      ...indicatorParams,
                      stdDev: parseFloat(e.target.value) || 2,
                    })
                  }
                  className="w-20 px-2 py-1.5 text-xs rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-[var(--lt-text)]"
                />
              </div>
            )}

            <button
              type="button"
              onClick={handleAddIndicator}
              className="px-3 py-1.5 text-xs rounded-md bg-[var(--lt-action)] text-white font-medium"
            >
              Add study
            </button>
            <button
              type="button"
              onClick={() => setShowIndicatorMenu(false)}
              className="px-3 py-1.5 text-xs rounded-md text-[var(--lt-muted)] hover:bg-[var(--lt-surface)]"
            >
              Cancel
            </button>
          </div>
        )}

        <div className="relative bg-[#0B0E14] flex">
          <div className="relative flex-1 min-w-0">
            {error && (
              <div className="absolute inset-x-0 top-0 z-10 px-3 py-2 text-xs text-[var(--lt-bear)] bg-[var(--lt-bear-dim)] border-b border-[var(--lt-border)]">
                {error}
              </div>
            )}
            {loading && bars.length === 0 && (
              <div className="absolute inset-0 z-10 flex items-center justify-center text-[var(--lt-muted)]">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                Loading {symbol}…
              </div>
            )}
            {loadingMore && (
              <div className="absolute left-3 bottom-3 z-10 px-2 py-1 rounded border border-[var(--lt-border)] bg-[var(--lt-surface)]/90 text-[11px] text-[var(--lt-muted)]">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1.5" />
                Loading older bars…
              </div>
            )}
            <div
              ref={chartContainerRef}
              className="w-full"
              style={{ height: chartHeight, cursor: drawTool === 'fib' ? 'crosshair' : undefined }}
            />
            <div className="pointer-events-none absolute left-3 top-3 text-[11px] font-mono text-[var(--lt-faint)] opacity-70">
              {symbol} · {timeframe}
            </div>
          </div>
          <DayTapePanel
            open={dayTapeOpen}
            onClose={() => setDayTapeOpen(false)}
            symbol={symbol}
            fills={dayFills}
            summary={daySummary}
            loading={dayFillsLoading}
            onRefresh={loadDayFills}
            venue={executionVenue}
            intradayPl={intradayPl}
          />
          <Level1Panel
            quote={l1Quote}
            live={streamStatus === 'live' && l1Quote?.source === 'stream'}
            symbol={symbol}
            orderQty={orderQty}
            onQtyChange={setOrderQty}
            tradeMode={tradeMode}
            onTradeModeChange={setTradeMode}
            limitPrice={limitPrice}
            onLimitPriceChange={setLimitPrice}
            tpPrice={tpPrice}
            onTpPriceChange={setTpPrice}
            slPrice={slPrice}
            onSlPriceChange={setSlPrice}
            alertType={alertType}
            onAlertTypeChange={setAlertType}
            alertThreshold={alertThreshold}
            onAlertThresholdChange={setAlertThreshold}
            onBuy={() => submitChartOrder('buy')}
            onSell={() => submitChartOrder('sell')}
            onFlatten={flattenPosition}
            onSetAlert={setChartAlert}
            onSnapPrices={snapChartPrices}
            orderBusy={orderBusy}
            flattenBusy={flattenBusy}
            position={position}
            executionVenue={executionVenue}
            dayTapeOpen={dayTapeOpen}
            onToggleDayTape={() => setDayTapeOpen((v) => !v)}
            daySummary={daySummary}
            tradeDisabled={
              orderBusy ||
              flattenBusy ||
              !symbol ||
              (executionVenue === 'schwab' && (!schwabConnected || !schwabAccountNumber))
            }
            flattenDisabled={
              flattenBusy ||
              orderBusy ||
              !position ||
              (executionVenue === 'schwab' && (!schwabConnected || !schwabAccountNumber))
            }
          />
        </div>
      </div>
    </main>
  );
};

export default AdvancedChartingPage;
